import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ViewPaymentDetailsModal from './ViewPaymentDetailsModal';
import './Payments.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Dashboard tokens are stored in sessionStorage (api.js migrates them there).
// Keep a localStorage fallback for older sessions.
const getDashboardToken = () =>
  sessionStorage.getItem('token') ||
  sessionStorage.getItem('adminToken') ||
  localStorage.getItem('adminToken') ||
  localStorage.getItem('token');

// Syllera pricing (DA) – Individual & Family each with Monthly/Yearly; both can be selected per service
const PAYMENT_FORM_PRICES = {
  educational: {
    primary: { individual: { monthly: 400, yearly: 1200 }, family: { monthly: 1000, yearly: 3000 } },
    middle: { individual: { monthly: 600, yearly: 1800 }, family: { monthly: 1400, yearly: 4200 } },
    high: { individual: { monthly: 800, yearly: 2400 }, family: { monthly: 2000, yearly: 6000 } }
  },
  exams: {
    ielts: { individual: { monthly: 1400, yearly: 4200 }, family: { monthly: 3000, yearly: 9000 } },
    toefl: { individual: { monthly: 1400, yearly: 4200 }, family: { monthly: 3000, yearly: 9000 } }
  }
};

const DEFAULT_PLANS = () => ({ individual: { enabled: false, duration: 'yearly' }, family: { enabled: false, duration: 'yearly' } });
const EDUCATIONAL_YEARS = { primary: [1, 2, 3, 4, 5], middle: [1, 2, 3, 4], high: [1, 2, 3] };

/** Compress year list to range format: [1,2,3,5] → "1–3, 5" */
function formatYearsRange(years) {
  if (!years || years.length === 0) return '';
  const sorted = [...years].sort((a, b) => a - b);
  const parts = [];
  let start = sorted[0], end = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    if (i < sorted.length && sorted[i] === end + 1) end = sorted[i];
    else {
      parts.push(start === end ? `${start}` : `${start}–${end}`);
      if (i < sorted.length) { start = sorted[i]; end = sorted[i]; }
    }
  }
  return parts.join(', ');
}

const Payments = (props) => {
  const { t } = useTranslation('navigation');
  const { initialTab: initialTabProp, singleTab: singleTabProp } = props || {};

  // When singleTab is set (e.g. School dashboard), show only that tab and hide the internal tab bar
  const isSingleTabView = Boolean(singleTabProp);

  // Get user role from localStorage to set initial tab
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = (user.role || 'admin').toLowerCase();

  // Set initial tab based on role (agents/moderators/school can't see 'plans' or 'dashboard-people')
  const getInitialTab = () => {
    if (userRole === 'admin') {
      return 'plans';
    }
    return 'manual-payments';
  };

  const [activeTab, setActiveTab] = useState(
    isSingleTabView ? singleTabProp : (initialTabProp || getInitialTab())
  );

  // When parent passes initialTab (e.g. School dashboard opening Receipts), sync when it changes
  useEffect(() => {
    if (initialTabProp && initialTabProp !== activeTab && !isSingleTabView) {
      setActiveTab(initialTabProp);
    }
  }, [initialTabProp, isSingleTabView]);

  // In single-tab view (School sidebar), keep activeTab fixed to singleTab
  useEffect(() => {
    if (isSingleTabView && singleTabProp && activeTab !== singleTabProp) {
      setActiveTab(singleTabProp);
    }
  }, [isSingleTabView, singleTabProp, activeTab]);
  
  // Helper function to translate plan features
  const translateFeature = (feature) => {
    if (!feature) return '';
    const featureMap = {
      'Complete vocabulary for any educational level': t('payments.subscriptionPlans.planFeatures.completeVocabularyEducational'),
      'Interactive exercises': t('payments.subscriptionPlans.planFeatures.interactiveExercises'),
      'Progress tracking': t('payments.subscriptionPlans.planFeatures.progressTracking'),
      'Practice tests': t('payments.subscriptionPlans.planFeatures.practiceTests'),
      'One educational level access': t('payments.subscriptionPlans.planFeatures.oneEducationalLevelAccess'),
      '12 months duration': t('payments.subscriptionPlans.planFeatures.twelveMonthsDuration'),
      'Complete vocabulary for any international exam': t('payments.subscriptionPlans.planFeatures.completeVocabularyExam'),
      'IELTS, TOEFL, SAT, GRE preparation': t('payments.subscriptionPlans.planFeatures.examPreparation'),
      'Exam-specific exercises': t('payments.subscriptionPlans.planFeatures.examSpecificExercises'),
      'One exam prep set': t('payments.subscriptionPlans.planFeatures.oneExamPrepSet'),
      'Complete vocabulary for any specific purpose': t('payments.subscriptionPlans.planFeatures.completeVocabularyESP'),
      'Medical, Engineering, Business, Legal English': t('payments.subscriptionPlans.planFeatures.fieldSpecific'),
      'Field-specific exercises': t('payments.subscriptionPlans.planFeatures.fieldSpecificExercises'),
      'One field access': t('payments.subscriptionPlans.planFeatures.oneFieldAccess'),
      'Complete reading practice for any level': t('payments.subscriptionPlans.planFeatures.completeReadingPractice'),
      'Beginner to Advanced articles': t('payments.subscriptionPlans.planFeatures.beginnerToAdvanced'),
      'Comprehension exercises': t('payments.subscriptionPlans.planFeatures.comprehensionExercises'),
      'One level access': t('payments.subscriptionPlans.planFeatures.oneLevelAccess'),
      'Complete dictation practice for any level': t('payments.subscriptionPlans.planFeatures.completeDictationPractice'),
      'Basic to Advanced audio exercises': t('payments.subscriptionPlans.planFeatures.basicToAdvancedAudio'),
      'Writing practice': t('payments.subscriptionPlans.planFeatures.writingPractice')
    };
    return featureMap[feature] || feature;
  };
  
  // Helper function to translate plan names
  const translatePlanName = (name) => {
    if (!name) return '';
    const nameMap = {
      'Educational Levels Vocabulary': t('payments.subscriptionPlans.planNames.educationalLevelsVocabulary'),
      'International Exam Vocabulary': t('payments.subscriptionPlans.planNames.internationalExamVocabulary'),
      'ESP Vocabulary': t('payments.subscriptionPlans.planNames.espVocabulary'),
      'Reading Practice': t('payments.subscriptionPlans.planNames.readingPractice'),
      'Dictation': t('payments.subscriptionPlans.planNames.dictation')
    };
    return nameMap[name] || name;
  };
  
  // Helper function to translate status
  const translateStatus = (status) => {
    if (!status) return '';
    const statusMap = {
      'active': t('payments.subscriptionPlans.filters.active'),
      'inactive': t('payments.subscriptionPlans.filters.inactive'),
      'draft': t('payments.subscriptionPlans.filters.draft'),
      'completed': t('payments.transactions.filters.completed'),
      'pending': t('payments.transactions.filters.pending'),
      'failed': t('payments.transactions.filters.failed'),
      'refunded': t('payments.transactions.filters.refunded'),
      'approved': t('payments.manualPayments.filters.approved'),
      'rejected': t('payments.manualPayments.filters.rejected')
    };
    return statusMap[status.toLowerCase()] || status;
  };
  
  // Helper function to translate payment methods
  const translatePaymentMethod = (method) => {
    if (!method) return '';
    const methodMap = {
      'credit_card': t('payments.transactions.paymentMethods.creditCard'),
      'paypal': t('payments.transactions.paymentMethods.paypal'),
      'bank_transfer': t('payments.transactions.paymentMethods.bankTransfer')
    };
    return methodMap[method.toLowerCase()] || method.replace('_', ' ');
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Stage 7: Subscription tab has its own search/filter (does not affect other tabs)
  const [plansSearchTerm, setPlansSearchTerm] = useState('');
  const [plansStatusFilter, setPlansStatusFilter] = useState('all');
  const [showPlanBuilder, setShowPlanBuilder] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Subscription plans from API (Stage 3 — single source of truth)
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(null);

  // Active plans for Create Payment Request form (Stage 6)
  const [activePlansForForm, setActivePlansForForm] = useState([]);
  // Language+Plan blocks for a single payment request (one plan per language block)
  const [planBlocks, setPlanBlocks] = useState([{ languageCode: 'en', planId: null }]);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [receiptSearchTerm, setReceiptSearchTerm] = useState('');
  const [receiptStatusFilter, setReceiptStatusFilter] = useState('all');

  // Subscription analytics (admin) + My subscription (current user)
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [subscriptionStatsLoading, setSubscriptionStatsLoading] = useState(false);
  const [mySubscription, setMySubscription] = useState(null);
  const [mySubscriptionLoading, setMySubscriptionLoading] = useState(false);
  
  // Provisional Receipt State
  const [provisionalReceipt, setProvisionalReceipt] = useState(null); // Stores receipt data after successful creation
  const [showReceiptPreview, setShowReceiptPreview] = useState(false); // Controls receipt preview modal
  const [printingReceipt, setPrintingReceipt] = useState(false); // Loading state for print
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [addUserFormData, setAddUserFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    role: 'student',
    status: 'active'
  });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState(null);

  // Create Payment Request – structured form state
  const [paymentFormDate, setPaymentFormDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [paymentFormCurrency, setPaymentFormCurrency] = useState('DA');
  const [paymentFormNotes, setPaymentFormNotes] = useState('');
  const [paymentFormPromo, setPaymentFormPromo] = useState('');
  const [paymentFormReceiptFile, setPaymentFormReceiptFile] = useState(null);
  const [sendInvoiceConfirm, setSendInvoiceConfirm] = useState(null);
  const [sendInvoiceSending, setSendInvoiceSending] = useState(false);
  
  // Customer search state
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  
  // Dashboard data state
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Plan Builder state (Stage 4: create + edit)
  const DEFAULT_PLAN_BUILDER = {
    name: '',
    languageCode: 'all',
    featureType: 'multi',       // 'single' | 'multi' — Single Feature / Multi-Feature
    userType: 'individual',     // 'individual' | 'family'
    maxUsers: 4,                // optional, shown when userType === 'family'
    price: '',
    currency: 'DA',
    cycle: 'year',
    accessScope: '',
    description: '',
    descriptionAutoFilled: true, // if true, description is synced from features; user edit sets false
    selectedFeatures: [],
    selectedEducationalYearLevelIds: [],
    selectedExamCodes: [],
    trialOption: '',
    icon: '',
    isPopular: false
  };
  const PLAN_BUILDER_EXAM_OPTIONS = ['IELTS', 'TOEFL'];
  // Other features (no scope): ids that are not educational_vocab or exam_vocab
  const PLAN_BUILDER_OTHER_FEATURE_IDS = ['general_vocab', 'academic_vocab_esp', 'dictation', 'reading', 'vocabulary_placement_test', 'virtual_classroom'];
  const [planBuilderData, setPlanBuilderData] = useState({ ...DEFAULT_PLAN_BUILDER });
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planBuilderSaving, setPlanBuilderSaving] = useState(false);
  const [planBuilderError, setPlanBuilderError] = useState(null);
  const [yearLevelsForPlanBuilder, setYearLevelsForPlanBuilder] = useState([]);
  
  // Available features for plan builder (ids must match Backend FEATURE_TO_MY_COURSES / hasFeatureAccess)
  const availableFeatures = [
    { id: 'educational_vocab', name: 'Educational levels\' vocabulary', icon: 'school' },
    { id: 'exam_vocab', name: 'International exam\'s vocabulary', icon: 'assignment' },
    { id: 'general_vocab', name: 'General vocabulary', icon: 'menu_book' },
    { id: 'academic_vocab_esp', name: 'Academic vocabulary (ESP)', icon: 'work' },
    { id: 'dictation', name: 'Dictation', icon: 'record_voice_over' },
    { id: 'reading', name: 'Reading', icon: 'menu_book' },
    { id: 'vocabulary_placement_test', name: 'Vocabulary placement test', icon: 'assessment' },
    { id: 'virtual_classroom', name: 'Virtual classroom', icon: 'videocam' }
  ];

  // Languages (built-in + optional custom languages persisted in localStorage)
  const BUILTIN_LANGUAGES = useMemo(() => ([
    { code: 'all', label: 'All languages' },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'it', label: 'Italian' },
    { code: 'es', label: 'Spanish' },
    { code: 'ar', label: 'Arabic' },
  ]), []);

  const [customLanguages, setCustomLanguages] = useState(() => {
    try {
      const raw = localStorage.getItem('syllera_custom_languages');
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((x) => ({ code: String(x.code || '').toLowerCase().trim(), label: String(x.label || '').trim() }))
        .filter((x) => x.code && x.label);
    } catch (_) {
      return [];
    }
  });

  useEffect(() => {
    try { localStorage.setItem('syllera_custom_languages', JSON.stringify(customLanguages)); } catch (_) {}
  }, [customLanguages]);

  const languageOptions = useMemo(() => {
    const built = BUILTIN_LANGUAGES;
    const builtCodes = new Set(built.map((l) => l.code));
    const customs = (customLanguages || []).filter((l) => !builtCodes.has(l.code));
    return [...built, ...customs].sort((a, b) => (a.code === 'all' ? -1 : b.code === 'all' ? 1 : a.label.localeCompare(b.label)));
  }, [BUILTIN_LANGUAGES, customLanguages]);

  // "Add language" as dropdown selection (no code/label input)
  const EXTRA_LANGUAGES = useMemo(() => ([
    { code: 'tr', label: 'Turkish' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'ru', label: 'Russian' },
    { code: 'zh', label: 'Chinese' },
    { code: 'ja', label: 'Japanese' },
    { code: 'ko', label: 'Korean' },
    { code: 'nl', label: 'Dutch' },
    { code: 'sv', label: 'Swedish' },
  ]), []);

  const [showAddLanguagePicker, setShowAddLanguagePicker] = useState({ open: false, target: 'payment' }); // 'payment' | 'planBuilder'
  const [pickedExtraLanguage, setPickedExtraLanguage] = useState('');

  const openAddLanguagePicker = (target) => {
    setPickedExtraLanguage('');
    setShowAddLanguagePicker({ open: true, target: target === 'planBuilder' ? 'planBuilder' : 'payment' });
  };
  const closeAddLanguagePicker = () => {
    setPickedExtraLanguage('');
    setShowAddLanguagePicker((prev) => ({ ...prev, open: false }));
  };
  const availableExtraLanguages = useMemo(() => {
    const existing = new Set(languageOptions.map((l) => l.code));
    return EXTRA_LANGUAGES.filter((l) => !existing.has(l.code));
  }, [EXTRA_LANGUAGES, languageOptions]);

  const applyPickedLanguage = (code) => {
    const entry = EXTRA_LANGUAGES.find((l) => l.code === code);
    if (!entry) return;
    setCustomLanguages((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const idx = next.findIndex((l) => (l.code || '').toLowerCase() === entry.code);
      if (idx >= 0) next[idx] = entry;
      else next.push(entry);
      return next;
    });
    if (showAddLanguagePicker.target === 'planBuilder') {
      setPlanBuilderData((prev) => ({ ...prev, languageCode: entry.code }));
    } else {
      // Apply to the first block that has no language chosen (or append a new block).
      setPlanBlocks((prev) => {
        const next = Array.isArray(prev) && prev.length ? [...prev] : [{ languageCode: 'en', planId: null }];
        const idx = next.findIndex((b) => !b.languageCode);
        if (idx >= 0) next[idx] = { ...next[idx], languageCode: entry.code, planId: null };
        else next.push({ languageCode: entry.code, planId: null });
        return next;
      });
    }
    closeAddLanguagePicker();
  };

  // Map duration_days to cycle value for form
  const durationDaysToCycle = (days) => {
    const d = parseInt(days, 10);
    if (d >= 365) return 'year';
    if (d >= 180) return '6months';
    return 'month';
  };
  const cycleToDurationDays = (cycle) => {
    if (cycle === 'year') return 365;
    if (cycle === '6months') return 180;
    return 30;
  };

  const openPlanBuilderForCreate = () => {
    setPlanBuilderData({ ...DEFAULT_PLAN_BUILDER });
    setEditingPlanId(null);
    setPlanBuilderError(null);
    setShowPlanBuilder(true);
  };

  // Auto-fill description from selected features (editable; user edit sets descriptionAutoFilled false)
  useEffect(() => {
    if (!showPlanBuilder) return;
    setPlanBuilderData((prev) => {
      if (!prev.descriptionAutoFilled) return prev;
      const next = (() => {
        const parts = [];
        if ((prev.selectedFeatures || []).includes('educational_vocab')) {
          const n = (prev.selectedEducationalYearLevelIds || []).length;
          parts.push(n ? `Educational levels (${n} year level${n > 1 ? 's' : ''})` : 'Educational levels vocabulary');
        }
        if ((prev.selectedFeatures || []).includes('exam_vocab')) {
          const codes = (prev.selectedExamCodes || []).length ? prev.selectedExamCodes.join(', ') : 'International exams';
          parts.push(codes);
        }
        (prev.selectedFeatures || []).forEach((id) => {
          if (id === 'educational_vocab' || id === 'exam_vocab') return;
          const f = availableFeatures.find((x) => x.id === id);
          if (f) parts.push(f.name);
        });
        return parts.length ? parts.join('; ') + '.' : '';
      })();
      if (!next || next === prev.description) return prev;
      return { ...prev, description: next };
    });
  }, [showPlanBuilder, planBuilderData.selectedFeatures, planBuilderData.selectedEducationalYearLevelIds, planBuilderData.selectedExamCodes]);

  const openPlanBuilderForEdit = (plan) => {
    const raw = plan.features_json;
    let parsed = Array.isArray(raw) ? raw : (typeof raw === 'string' ? (() => { try { return JSON.parse(raw || '[]'); } catch { return []; } })() : []);
    if (!Array.isArray(parsed) && parsed && typeof parsed === 'object') {
      const featureIds = Object.keys(parsed);
      const selectedEducationalYearLevelIds = Array.isArray(parsed.educational_vocab?.year_level_ids) ? parsed.educational_vocab.year_level_ids.map(Number) : [];
      const selectedExamCodes = Array.isArray(parsed.exam_vocab?.exam_codes) ? parsed.exam_vocab.exam_codes : [];
      const userType = (plan.type || 'individual') === 'family' ? 'family' : 'individual';
      setPlanBuilderData({
        name: plan.name || '',
        languageCode: plan.language_code || 'all',
        featureType: featureIds.length <= 1 ? 'single' : 'multi',
        userType,
        maxUsers: plan.max_users != null ? Number(plan.max_users) : 4,
        price: plan.price != null ? String(plan.price) : '',
        currency: plan.currency || 'DA',
        cycle: durationDaysToCycle(plan.duration_days),
        accessScope: plan.target || plan.category || '',
        description: plan.description || '',
        descriptionAutoFilled: false,
        selectedFeatures: featureIds.length ? featureIds : [],
        selectedEducationalYearLevelIds,
        selectedExamCodes,
        trialOption: '',
        icon: '',
        isPopular: false
      });
      setEditingPlanId(plan.id);
      setPlanBuilderError(null);
      setShowPlanBuilder(true);
      return;
    }
    const features = Array.isArray(parsed) ? parsed : [];
    let selectedFeatures = availableFeatures.filter((f) => features.some((x) => String(x) === f.id)).map((f) => f.id);
    if (features.length && selectedFeatures.length === 0) {
      features.forEach((x) => {
        const byId = availableFeatures.find((f) => f.id === String(x));
        const byName = availableFeatures.find((f) => String(x).toLowerCase().includes(f.name.toLowerCase()) || f.name.toLowerCase().includes(String(x).toLowerCase()));
        const match = byId || byName;
        if (match && !selectedFeatures.includes(match.id)) selectedFeatures.push(match.id);
      });
    }
    const userType = (plan.type || 'individual') === 'family' ? 'family' : 'individual';
    setPlanBuilderData({
      name: plan.name || '',
      languageCode: plan.language_code || 'all',
      featureType: selectedFeatures.length <= 1 ? 'single' : 'multi',
      userType,
      maxUsers: plan.max_users != null ? Number(plan.max_users) : 4,
      price: plan.price != null ? String(plan.price) : '',
      currency: plan.currency || 'DA',
      cycle: durationDaysToCycle(plan.duration_days),
      accessScope: plan.target || plan.category || '',
      description: plan.description || '',
      descriptionAutoFilled: false,
      selectedFeatures: selectedFeatures.length ? selectedFeatures : [],
      selectedEducationalYearLevelIds: [],
      selectedExamCodes: [],
      trialOption: '',
      icon: '',
      isPopular: false
    });
    setEditingPlanId(plan.id);
    setPlanBuilderError(null);
    setShowPlanBuilder(true);
  };

  const closePlanBuilder = () => {
    setShowPlanBuilder(false);
    setEditingPlanId(null);
    setPlanBuilderData({ ...DEFAULT_PLAN_BUILDER });
    setPlanBuilderError(null);
  };

  useEffect(() => {
    if (!showPlanBuilder) return;
    const token = getDashboardToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/api/year-levels`, { headers })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) setYearLevelsForPlanBuilder(data.data);
        else setYearLevelsForPlanBuilder([]);
      })
      .catch(() => setYearLevelsForPlanBuilder([]));
  }, [showPlanBuilder]);

  // Stage 8: Plan Builder submit valid (name + price)
  const planBuilderCanSubmit = useMemo(() => {
    const nameOk = (planBuilderData.name || '').trim().length > 0;
    const priceNum = parseFloat(planBuilderData.price);
    const priceOk = !isNaN(priceNum) && priceNum >= 0;
    return nameOk && priceOk;
  }, [planBuilderData.name, planBuilderData.price]);

  const buildPlanPayload = () => {
    const featureIds = (planBuilderData.selectedFeatures || [])
      .filter((id) => availableFeatures.some((f) => f.id === id));
    // Always store explicit scope (structured object). educational_vocab: empty year_level_ids = no access (strict rule). Select at least one year level in Plan Builder.
    const features_json = featureIds.reduce((acc, id) => {
      if (id === 'educational_vocab') acc[id] = { year_level_ids: (planBuilderData.selectedEducationalYearLevelIds || []).map(Number) };
      else if (id === 'exam_vocab') acc[id] = { exam_codes: (planBuilderData.selectedExamCodes || []).slice() };
      else acc[id] = {};
      return acc;
    }, {});
    const userType = planBuilderData.userType || 'individual';
    return {
      // Avoid collisions: same plan name used twice should not 409 due to duplicate code.
      code: editingPlanId
        ? undefined
        : (() => {
            const base = (planBuilderData.name || 'PLAN')
              .trim()
              .toUpperCase()
              .replace(/[^A-Z0-9]+/g, '_')
              .replace(/^_+|_+$/g, '')
              .slice(0, 60);
            return `${base || 'PLAN'}_${Date.now()}`;
          })(),
      name: planBuilderData.name.trim(),
      language_code: (planBuilderData.languageCode || 'all'),
      description: planBuilderData.description.trim() || null,
      type: userType === 'family' ? 'family' : 'individual',
      category: planBuilderData.accessScope.trim() || planBuilderData.name.trim() || null,
      target: planBuilderData.accessScope.trim() || null,
      max_users: userType === 'family' ? (parseInt(planBuilderData.maxUsers, 10) || 4) : 1,
      duration_days: cycleToDurationDays(planBuilderData.cycle),
      features_json,
      price: parseFloat(planBuilderData.price) || 0,
      currency: planBuilderData.currency || 'DA',
      is_active: true,
      sort_order: plans.length
    };
  };

  const savePlanBuilder = async () => {
    if (!planBuilderData.name?.trim()) {
      setPlanBuilderError(t('payments.subscriptionPlans.planBuilderModal.planNameRequired'));
      return;
    }
    const price = parseFloat(planBuilderData.price);
    if (isNaN(price) || price < 0) {
      setPlanBuilderError(t('payments.subscriptionPlans.planBuilderModal.validPriceRequired'));
      return;
    }
    // Strict scope: if Educational Levels Vocabulary is included, at least one year level must be selected (empty = no access).
    if (planBuilderData.selectedFeatures?.includes('educational_vocab')) {
      const ids = planBuilderData.selectedEducationalYearLevelIds || [];
      if (!ids.length) {
        setPlanBuilderError(t('payments.subscriptionPlans.planBuilderModal.selectAtLeastOneYearLevel') || 'Select at least one year level for Educational Levels Vocabulary. Empty scope means no access to educational content.');
        return;
      }
    }
    setPlanBuilderSaving(true);
    setPlanBuilderError(null);
    const token = getDashboardToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const payload = buildPlanPayload();
      if (editingPlanId) {
        const res = await fetch(`${API_BASE_URL}/api/plans/${editingPlanId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload)
        });
        const json = await res.json();
        if (!res.ok) {
          setPlanBuilderError(json.message || t('payments.subscriptionPlans.planBuilderModal.failedToUpdate'));
          return;
        }
        await fetchPlans();
        alert(t('payments.subscriptionPlans.planBuilderModal.planUpdatedSuccess') || 'Plan updated successfully.');
        closePlanBuilder();
      } else {
        const res = await fetch(`${API_BASE_URL}/api/plans`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });
        const json = await res.json();
        if (!res.ok) {
          setPlanBuilderError(json.message || t('payments.subscriptionPlans.planBuilderModal.failedToCreate'));
          return;
        }
        await fetchPlans();
        alert(t('payments.subscriptionPlans.planBuilderModal.planCreatedSuccess') || 'Plan created successfully.');
        closePlanBuilder();
      }
    } catch (e) {
      setPlanBuilderError(e.message || t('payments.subscriptionPlans.planBuilderModal.requestFailed'));
    } finally {
      setPlanBuilderSaving(false);
    }
  };

  // Stage 5: Toggle is_active (Pause / Activate)
  const handleTogglePlan = async (plan) => {
    const token = getDashboardToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/plans/${plan.id}/toggle`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) await fetchPlans();
      else {
        const json = await res.json();
        alert(json.message || t('payments.subscriptionPlans.failedToTogglePlan'));
      }
    } catch (e) {
      alert(e.message || t('payments.subscriptionPlans.planBuilderModal.requestFailed'));
    }
  };

  // Delete plan (DELETE; only if no subscriptions)
  const handleDeletePlan = async (plan) => {
    const token = getDashboardToken();
    if (!token) return;
    const msg = t('payments.subscriptionPlans.actions.deletePlanConfirm') || `Delete plan "${plan.name}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/plans/${plan.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (res.ok && json.success) {
        await fetchPlans();
      } else {
        alert(json.message || t('payments.subscriptionPlans.failedToDeletePlan') || 'Failed to delete plan.');
      }
    } catch (e) {
      alert(e.message || t('payments.subscriptionPlans.planBuilderModal.requestFailed'));
    }
  };

  // Stage 5: Duplicate plan (POST copy, is_active: false). Preserve explicit scope (structured features_json).
  const handleDuplicatePlan = async (plan) => {
    const token = getDashboardToken();
    if (!token) return;
    const raw = plan.features_json;
    let features_json = typeof raw === 'string' ? (() => { try { return JSON.parse(raw || '{}'); } catch { return {}; } })() : (raw || {});
    if (Array.isArray(features_json)) {
      features_json = features_json.reduce((acc, id) => {
        acc[id] = id === 'educational_vocab' ? { year_level_ids: [] } : {};
        return acc;
      }, {});
    }
    const copy = {
      code: `${(plan.code || 'plan').replace(/\s+/g, '_')}_${Date.now()}`,
      name: `Copy of ${plan.name}`,
      description: plan.description || null,
      type: plan.type || 'individual',
      category: plan.category || null,
      target: plan.target || null,
      max_users: plan.max_users != null ? plan.max_users : 1,
      duration_days: plan.duration_days != null ? plan.duration_days : 365,
      features_json,
      price: plan.price,
      currency: plan.currency || 'DA',
      is_active: false,
      sort_order: (plan.sort_order != null ? plan.sort_order : 0) + 1
    };
    try {
      const res = await fetch(`${API_BASE_URL}/api/plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(copy)
      });
      const json = await res.json();
      if (res.ok && json.success) {
        await fetchPlans();
      } else {
        alert(json.message || t('payments.subscriptionPlans.failedToDuplicatePlan'));
      }
    } catch (e) {
      alert(e.message || t('payments.subscriptionPlans.planBuilderModal.requestFailed'));
    }
  };

  // API functions
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔄 Fetching dashboard data...');
      const response = await fetch(`${API_BASE_URL}/api/dashboard-payments/overview`);
      
      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response not ok:', errorText);
        setError(`Server error: ${response.status} - ${errorText}`);
        return;
      }
      
      const result = await response.json();
      console.log('✅ Dashboard data received:', result);
      
      if (result.success) {
        setDashboardData(result.data);
      } else {
        setError('Failed to fetch dashboard data: ' + (result.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('❌ Fetch error:', err);
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard data when component mounts or when dashboard tab is active
  useEffect(() => {
    if (activeTab === 'dashboard-people') {
      fetchDashboardData();
    }
  }, [activeTab]);

  // Customer search function - query server with search params (avoid only searching latest 20 users)
  const searchCustomers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerSearchResults([]);
      setShowCustomerDropdown(false);
      return;
    }

    setSearchingCustomers(true);
    try {
      const token = getDashboardToken();
      const encodedSearch = encodeURIComponent(searchTerm.trim());
      const response = await fetch(`${API_BASE_URL}/api/admin/users?search=${encodedSearch}&page=1&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      const users = Array.isArray(data.users) ? data.users : [];
      const filtered = users.slice(0, 10);

      setCustomerSearchResults(filtered);
      setShowCustomerDropdown(filtered.length > 0);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchResults([]);
      setShowCustomerDropdown(false);
    } finally {
      setSearchingCustomers(false);
    }
  };

  // Debounced customer search - triggers searchCustomers function
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (customerSearchTerm && customerSearchTerm.length >= 2) {
        searchCustomers(customerSearchTerm);
      } else {
        setCustomerSearchResults([]);
        setShowCustomerDropdown(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearchTerm]);

  // Define all tabs with role restrictions
  const allTabs = [
    { id: 'dashboard-people', label: t('payments.tabs.dashboard'), icon: 'people', roles: ['admin'] },
    { id: 'plans', label: t('payments.tabs.subscriptionPlans'), icon: 'subscriptions', roles: ['admin'] },
    { id: 'transactions', label: t('payments.tabs.transactions'), icon: 'receipt', roles: ['admin', 'agent', 'moderator', 'school'] },
    { id: 'manual-payments', label: t('payments.tabs.manualPayments'), icon: 'payment', roles: ['admin', 'agent', 'moderator', 'school'] },
    { id: 'receipts', label: t('payments.tabs.receipts'), icon: 'description', roles: ['admin', 'agent', 'moderator', 'school'] }
  ];
  
  // Filter tabs based on user role
  const tabs = allTabs.filter(tab => {
    const allowedRoles = tab.roles || ['admin'];
    return allowedRoles.includes(userRole);
  });
  
  // If agent/moderator tries to access restricted tab, redirect to first available tab
  useEffect(() => {
    if (!tabs.find(tab => tab.id === activeTab)) {
      if (tabs.length > 0) {
        setActiveTab(tabs[0].id);
      }
    }
  }, [userRole, activeTab]);

  // Refetch plans (used after create/update/toggle/duplicate)
  const fetchPlans = async () => {
    if (activeTab !== 'plans') return;
    const token = getDashboardToken();
    setPlansLoading(true);
    setPlansError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/plans`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const json = await res.json();
      if (res.ok && json.success && Array.isArray(json.data)) {
        setPlans(json.data);
      } else {
        setPlans([]);
        setPlansError(json.message || 'Failed to load plans');
      }
    } catch (e) {
      setPlans([]);
      setPlansError('Failed to load plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchSubscriptionStats = async () => {
    const token = getDashboardToken();
    if (!token) return;
    setSubscriptionStatsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/subscription-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (res.ok && json.success && json.data) {
        setSubscriptionStats(json.data);
      } else {
        setSubscriptionStats(null);
      }
    } catch (e) {
      setSubscriptionStats(null);
    } finally {
      setSubscriptionStatsLoading(false);
    }
  };

  const fetchMySubscription = async () => {
    const token = getDashboardToken();
    if (!token) return;
    setMySubscriptionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/me/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setMySubscription(json.data || null);
      } else {
        setMySubscription(null);
      }
    } catch (e) {
      setMySubscription(null);
    } finally {
      setMySubscriptionLoading(false);
    }
  };

  // Stage 3: Fetch plans from API when Subscription Plans tab is active
  useEffect(() => {
    if (activeTab !== 'plans') return;
    fetchPlans();
  }, [activeTab]);

  // Fetch subscription stats and "my subscription" when plans tab is active
  useEffect(() => {
    if (activeTab !== 'plans') return;
    fetchSubscriptionStats();
    fetchMySubscription();
  }, [activeTab]);

  // Stage 6: Fetch active plans when Create Payment Request form is opened
  useEffect(() => {
    if (!showPaymentForm) return;
    const token = getDashboardToken();
    const fetchActivePlans = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/plans?active=true`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const json = await res.json();
        if (res.ok && json.success && Array.isArray(json.data)) {
          setActivePlansForForm(json.data);
          setPlanBlocks((prev) => {
            const next = Array.isArray(prev) && prev.length ? [...prev] : [{ languageCode: 'en', planId: null }];
            return next.map((b) => {
              const ok = b.planId && json.data.some((p) => p.id === b.planId);
              return ok ? b : { ...b, planId: null };
            });
          });
        } else {
          setActivePlansForForm([]);
          setPlanBlocks([{ languageCode: 'en', planId: null }]);
        }
      } catch (e) {
        setActivePlansForForm([]);
        setPlanBlocks([{ languageCode: 'en', planId: null }]);
      }
    };
    fetchActivePlans();
  }, [showPaymentForm]);

  // Keep selected plans compatible with their block language
  useEffect(() => {
    if (!showPaymentForm) return;
    setPlanBlocks((prev) => {
      const next = Array.isArray(prev) && prev.length ? [...prev] : [{ languageCode: 'en', planId: null }];
      return next.map((b) => {
        if (!b.planId || !b.languageCode) return b;
        const p = activePlansForForm.find((x) => x.id === b.planId);
        if (!p) return { ...b, planId: null };
        const lc = (p.language_code || 'all').toLowerCase();
        const sel = String(b.languageCode).toLowerCase();
        // Strict: only exact match, no legacy 'all' when a specific language is chosen
        return lc === sel ? b : { ...b, planId: null };
      });
    });
  }, [showPaymentForm, activePlansForForm]);

  // Stage 7: Filter plans by status and search (Subscription tab only)
  const filteredPlans = useMemo(() => {
    let list = plans;
    if (plansStatusFilter === 'active') list = list.filter((p) => p.is_active);
    else if (plansStatusFilter === 'inactive' || plansStatusFilter === 'draft') list = list.filter((p) => !p.is_active);
    const term = (plansSearchTerm || '').trim().toLowerCase();
    if (term) {
      list = list.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(term)) ||
          (p.code && p.code.toLowerCase().includes(term)) ||
          (p.category && p.category.toLowerCase().includes(term)) ||
          (p.target && p.target.toLowerCase().includes(term))
      );
    }
    return list;
  }, [plans, plansSearchTerm, plansStatusFilter]);

  const transactions = [
    {
      id: 'TXN001',
      user: 'John Doe',
      plan: 'Premium Plan',
      amount: 59.99,
      status: 'completed',
      date: '2024-01-15',
      paymentMethod: 'credit_card'
    },
    {
      id: 'TXN002',
      user: 'Jane Smith',
      plan: 'Basic Plan',
      amount: 29.99,
      status: 'pending',
      date: '2024-01-14',
      paymentMethod: 'paypal'
    },
    {
      id: 'TXN003',
      user: 'Mike Johnson',
      plan: 'Enterprise Plan',
      amount: 199.99,
      status: 'completed',
      date: '2024-01-13',
      paymentMethod: 'bank_transfer'
    }
  ];

  // Manual payments state - fetched from backend
  const [manualPayments, setManualPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentsError, setPaymentsError] = useState(null);

  // Fetch manual payments from backend API
  const fetchManualPayments = async () => {
    setLoadingPayments(true);
    setPaymentsError(null);
    try {
      const token = getDashboardToken();
      const response = await fetch(`${API_BASE_URL}/api/manual-payments/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized - Please login again');
        }
        throw new Error('Failed to fetch manual payments');
      }

      const data = await response.json();
      if (data.success && data.data) {
        setManualPayments(data.data);
      } else {
        setManualPayments([]);
      }
    } catch (error) {
      console.error('❌ Error fetching manual payments:', error);
      setPaymentsError(error.message);
      // Keep empty array on error
      setManualPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Fetch payments when manual-payments tab or receipts tab is active
  useEffect(() => {
    if (activeTab === 'manual-payments' || activeTab === 'receipts') {
      fetchManualPayments();
    }
  }, [activeTab]);

  // Listen for tab switch events from Agent Overview
  useEffect(() => {
    const handleSwitchTab = (event) => {
      const targetTab = event.detail;
      if (targetTab && ['manual-payments', 'receipts', 'transactions'].includes(targetTab)) {
        setActiveTab(targetTab);
      }
    };

    const handleOpenForm = () => {
      if (activeTab === 'manual-payments') {
        setShowPaymentForm(true);
      }
    };

    window.addEventListener('switchToPaymentsTab', handleSwitchTab);
    window.addEventListener('openPaymentForm', handleOpenForm);

    return () => {
      window.removeEventListener('switchToPaymentsTab', handleSwitchTab);
      window.removeEventListener('openPaymentForm', handleOpenForm);
    };
  }, [activeTab]);

  // Create user from Manual Payments (for customers without an account)
  const getAuthHeaders = () => {
    const token = getDashboardToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const createUserFromPayments = async () => {
    if (!addUserFormData.first_name?.trim() || !addUserFormData.last_name?.trim() || !addUserFormData.email?.trim() || !addUserFormData.phone?.trim()) {
      setAddUserError(t('payments.manualPayments.addUserRequiredFields') || 'Please fill in First Name, Last Name, Email, and Phone');
      return;
    }
    try {
      setAddUserLoading(true);
      setAddUserError(null);
      // Use dedicated create-customer endpoint (allowed for agent + admin)
      const payload = {
        first_name: addUserFormData.first_name.trim(),
        last_name: addUserFormData.last_name.trim(),
        email: addUserFormData.email.trim(),
        phone: addUserFormData.phone.trim(),
        country: addUserFormData.country?.trim() || null,
        city: addUserFormData.city?.trim() || null
      };
      const response = await fetch(`${API_BASE_URL}/api/manual-payments/create-customer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) {
        const msg = data.message || data.error || 'Failed to create customer';
        throw new Error(msg);
      }
      const newUser = data.user;
      setShowAddUserForm(false);
      setAddUserFormData({ first_name: '', last_name: '', email: '', phone: '', country: '', city: '', role: 'student', status: 'active' });
      setAddUserError(null);
      setSelectedCustomer({
        id: newUser.id,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        email: newUser.email,
        phone: newUser.phone || ''
      });
      setCustomerSearchTerm(`${newUser.first_name} ${newUser.last_name}`.trim());
      setShowPaymentForm(true);
      const emailSent = data.passwordResetEmailSent === true;
      const successMsg = emailSent
        ? (t('payments.manualPayments.addUserSuccessEmailSent') || 'Customer created. A link to set their password was sent to their email. You can now add a payment.')
        : (t('payments.manualPayments.addUserSuccessEmailNotSent') || 'Customer created. The set-password email could not be sent—please tell them to use "Forgot password" on the login page. You can now add a payment.');
      if (typeof alert === 'function') alert(successMsg);
    } catch (err) {
      console.error('Create customer error:', err);
      setAddUserError(err.message);
    } finally {
      setAddUserLoading(false);
    }
  };

  const handlePrintProvisionalReceipt = async (format) => {
    if (!provisionalReceipt) return;
    const isReceipt = format === 'receipt';
    setPrintingReceipt(true);
    try {
      if (provisionalReceipt.payment_request_id) {
        try {
          const token = getDashboardToken();
          await fetch(`${API_BASE_URL}/api/manual-payments/${provisionalReceipt.payment_request_id}/log-print`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ receipt_number: provisionalReceipt.receipt_number })
          });
        } catch (err) {
          console.warn('Could not log print to backend:', err);
        }
      }
      const printContent = document.getElementById('provisional-receipt-print')?.innerHTML || '';
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setPrintingReceipt(false);
        return;
      }
      const receiptNum = provisionalReceipt.payment_request_number || 'Receipt';
      const baseStyles =
        'body{font-family:Arial,sans-serif;font-weight:500;background:#fff;color:#000}' +
        '.provisional-header{text-align:center;border-bottom:2px solid #000}' +
        '.provisional-header h2{margin:0;color:#000;font-weight:600}' +
        '.receipt-type-label{color:#333;margin:10px 0 0 0;font-weight:600}' +
        '.receipt-body{margin:15px 0}' +
        '.receipt-info-row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #ccc}' +
        '.receipt-info-row .label{font-weight:600;color:#333}' +
        '.receipt-info-row .value{color:#000}' +
        '.status-badge{padding:4px 10px;border-radius:2px;font-weight:600}' +
        '.status-pending{background:#000;color:#fff}' +
        '.receipt-footer{text-align:center;margin-top:20px;padding-top:15px;border-top:2px solid #000;color:#333}' +
        '.receipt-footer p{margin:0;font-weight:500}';
      const a4Styles =
        'body{padding:20px}.receipt-print-wrap{max-width:180mm;margin:0 auto}' +
        '.provisional-header{padding-bottom:15px;margin-bottom:20px}.provisional-header h2{font-size:22pt}' +
        '.receipt-type-label{font-size:14pt}.receipt-body .receipt-info-row{padding:10px 0}' +
        '@media print{@page{size:A4;margin:15mm}body{margin:0!important;padding:15mm!important;font-size:11pt}.receipt-print-wrap{max-width:100%!important}.no-print{display:none!important}}';
      const receiptStyles =
        'body{padding:20px;font-weight:600}.receipt-print-wrap{max-width:80mm;margin:0 auto}' +
        '.provisional-header{padding-bottom:15px;margin-bottom:20px}.provisional-header h2{font-size:18pt;font-weight:700}' +
        '.receipt-type-label{font-size:13pt;font-weight:700}' +
        '.receipt-info-row .label{font-weight:700}.receipt-info-row .value{font-weight:600}' +
        '.receipt-footer p{font-weight:600}' +
        '.receipt-info-row{padding:8px 0}' +
        '@media print{@page{size:80mm auto;margin:5mm}body{margin:0!important;padding:5mm!important;width:80mm!important;max-width:80mm!important;font-size:12pt!important;font-weight:600!important}.receipt-print-wrap{width:80mm!important;max-width:80mm!important}.provisional-header h2{font-size:18pt!important;font-weight:700!important}.receipt-type-label{font-size:13pt!important;font-weight:700!important}.receipt-info-row .label{font-weight:700!important}.receipt-info-row .value{font-weight:600!important}.receipt-footer p{font-weight:600!important}.no-print{display:none!important}}';
      const receiptPrintStyles = baseStyles + (isReceipt ? receiptStyles : a4Styles);
      printWindow.document.write(
        '<!DOCTYPE html><html><head><title>Receipt - ' + receiptNum + '</title><style>' + receiptPrintStyles + '</style></head><body><div class="receipt-print-wrap">' + printContent + '</div></body></html>'
      );
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        setPrintingReceipt(false);
      }, 250);
    } catch (error) {
      console.error('Print error:', error);
      alert('Error printing receipt: ' + error.message);
      setPrintingReceipt(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard-people':
        return (
          <div className="tab-content">
            <div className="content-header">
              <div className="header-left">
                <h3>{t('payments.dashboard.title')}</h3>
                <p className="header-subtitle">{t('payments.dashboard.subtitle')}</p>
              </div>
              <div className="header-actions">
                <button className="secondary-btn" onClick={fetchDashboardData}>
                  <span className="material-icons">refresh</span>
                  {t('payments.dashboard.refreshData')}
                </button>
                <button className="secondary-btn">
                  <span className="material-icons">analytics</span>
                  {t('payments.dashboard.detailedAnalytics')}
                </button>
                <button className="primary-btn">
                  <span className="material-icons">download</span>
                  {t('payments.dashboard.exportReport')}
                </button>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="loading-state">
                <p>{t('payments.dashboard.loadingDashboard')}</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="error-state">
                <span className="material-icons">error</span>
                <p>{error}</p>
                <button className="secondary-btn" onClick={fetchDashboardData}>
                  Retry
                </button>
              </div>
            )}

            {/* Dashboard Content */}
            {dashboardData && !loading && !error && (
              <>
                {/* Dashboard Stats */}
                <div className="plans-overview">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <span className="material-icons">people</span>
                    </div>
                    <div className="stat-content">
                      <h4>{t('payments.dashboard.metrics.totalSubscribers')}</h4>
                      <p className="stat-number">{dashboardData.totalSubscribers.toLocaleString()}</p>
                      <span className="stat-change positive">+12.5% this month</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <span className="material-icons">attach_money</span>
                    </div>
                    <div className="stat-content">
                      <h4>{t('payments.dashboard.metrics.monthlyRevenue')}</h4>
                      <p className="stat-number">${dashboardData.monthlyRevenue.toLocaleString()}</p>
                      <span className="stat-change positive">+8.3% this month</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <span className="material-icons">trending_up</span>
                    </div>
                    <div className="stat-content">
                      <h4>{t('payments.dashboard.metrics.conversionRate')}</h4>
                      <p className="stat-number">{dashboardData.conversionRate}%</p>
                      <span className="stat-change positive">+2.1% this month</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <span className="material-icons">cancel</span>
                    </div>
                    <div className="stat-content">
                      <h4>{t('payments.dashboard.metrics.churnRate')}</h4>
                      <p className="stat-number">{dashboardData.churnRate}%</p>
                      <span className="stat-change negative">+0.3% this month</span>
                    </div>
                  </div>
                </div>

                {/* Additional Dashboard Content */}
                <div className="dashboard-people-section">
                  <div className="section-header">
                    <h4>User Growth Trends</h4>
                    <p>Track subscription growth and user engagement over time</p>
                  </div>
                  
                  <div className="dashboard-grid">
                    <div className="dashboard-card">
                      <div className="card-header">
                        <h5>Subscription Growth</h5>
                        <span className="material-icons">trending_up</span>
                      </div>
                      <div className="card-content">
                        <div className="growth-metric">
                          <span className="metric-value">+{dashboardData.growthData.newSubscribers}</span>
                          <span className="metric-label">{t('payments.dashboard.growthData.newSubscribers')}</span>
                          <span className="metric-label">New subscribers this week</span>
                        </div>
                        <div className="growth-chart">
                          {dashboardData.growthData.weeklyGrowth.map((height, index) => (
                            <div 
                              key={index} 
                              className="chart-bar" 
                              style={{height: `${height}%`}}
                            ></div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="dashboard-card">
                      <div className="card-header">
                        <h5>{t('payments.dashboard.growthData.revenueTrends')}</h5>
                        <span className="material-icons">attach_money</span>
                      </div>
                      <div className="card-content">
                        <div className="revenue-metric">
                          <span className="metric-value">${dashboardData.monthlyRevenue.toLocaleString()}</span>
                          <span className="metric-label">Monthly recurring revenue</span>
                        </div>
                        <div className="revenue-breakdown">
                          <div className="breakdown-item">
                            <span className="item-label">Premium Plans</span>
                            <span className="item-value">${dashboardData.revenueBreakdown.premiumPlans.toLocaleString()}</span>
                          </div>
                          <div className="breakdown-item">
                            <span className="item-label">Basic Plans</span>
                            <span className="item-value">${dashboardData.revenueBreakdown.basicPlans.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="dashboard-card">
                      <div className="card-header">
                        <h5>User Engagement</h5>
                        <span className="material-icons">people</span>
                      </div>
                      <div className="card-content">
                        <div className="engagement-metric">
                          <span className="metric-value">{dashboardData.engagementStats.activeUsersPercent}%</span>
                          <span className="metric-label">Active users this month</span>
                        </div>
                        <div className="engagement-stats">
                          <div className="stat-item">
                            <span className="stat-label">Daily Active</span>
                            <span className="stat-value">{dashboardData.engagementStats.dailyActive.toLocaleString()}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Weekly Active</span>
                            <span className="stat-value">{dashboardData.engagementStats.weeklyActive.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="dashboard-card">
                      <div className="card-header">
                        <h5>Retention Metrics</h5>
                        <span className="material-icons">trending_up</span>
                      </div>
                      <div className="card-content">
                        <div className="retention-metric">
                          <span className="metric-value">{dashboardData.retentionMetrics.oneMonth}%</span>
                          <span className="metric-label">Monthly retention rate</span>
                        </div>
                        <div className="retention-breakdown">
                          <div className="retention-item">
                            <span className="item-label">1 Month</span>
                            <span className="item-value">{dashboardData.retentionMetrics.oneMonth}%</span>
                          </div>
                          <div className="retention-item">
                            <span className="item-label">3 Months</span>
                            <span className="item-value">{dashboardData.retentionMetrics.threeMonths}%</span>
                          </div>
                          <div className="retention-item">
                            <span className="item-label">6 Months</span>
                            <span className="item-value">{dashboardData.retentionMetrics.sixMonths}%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 'plans':
        return (
          <div className="tab-content">
            <div className="content-header">
              <div className="header-left">
                <h3>{t('payments.subscriptionPlans.title')}</h3>
                <p className="header-subtitle">{t('payments.subscriptionPlans.subtitle')}</p>
              </div>
              <div className="header-actions">
                <button type="button" className="secondary-btn" onClick={() => alert(t('payments.subscriptionPlans.analyticsComingSoon') || 'Analytics coming soon.')} title={t('payments.subscriptionPlans.analyticsComingSoon')}>
                  <span className="material-icons">analytics</span>
                  {t('payments.subscriptionPlans.analytics')}
                </button>
                <button className="primary-btn" onClick={openPlanBuilderForCreate}>
                  <span className="material-icons">build</span>
                  {t('payments.subscriptionPlans.planBuilder')}
                </button>
              </div>
            </div>

            {/* Subscription analytics widgets */}
            {subscriptionStatsLoading && <div className="subscription-stats-loading">{t('payments.subscriptionPlans.loading') || 'Loading…'}</div>}
            {!subscriptionStatsLoading && subscriptionStats && (
              <div className="subscription-stats-strip">
                <div className="stat-card">
                  <span className="stat-label">{t('payments.subscriptionPlans.activeSubscriptions') || 'Active subscriptions'}</span>
                  <span className="stat-value">{subscriptionStats.activeCount}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">{t('payments.subscriptionPlans.totalRevenue') || 'Total revenue'}</span>
                  <span className="stat-value">{Number(subscriptionStats.revenue).toLocaleString()} DA</span>
                </div>
                <div className="stat-card stat-card-wide">
                  <span className="stat-label">{t('payments.subscriptionPlans.subscribersPerPlan') || 'Subscribers per plan'}</span>
                  {Array.isArray(subscriptionStats.perPlan) && subscriptionStats.perPlan.length > 0 ? (
                    (() => {
                      const withSubscribers = subscriptionStats.perPlan.filter((row) => Number(row.activeSubscribers) > 0);
                      if (withSubscribers.length === 0) {
                        return <div className="per-plan-empty">{t('payments.subscriptionPlans.noSubscribersYet') || 'No active subscribers yet'}</div>;
                      }
                      return (
                        <div className="per-plan-list">
                          {withSubscribers.slice(0, 8).map((row) => (
                            <div key={row.id} className="per-plan-row">
                              <span className="plan-name">{row.name || row.code}</span>
                              <span className="plan-count">{row.activeSubscribers} active</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="per-plan-empty">{t('payments.subscriptionPlans.noSubscribersYet') || 'No active subscribers yet'}</div>
                  )}
                </div>
              </div>
            )}

            {/* My subscription (current user) + Renew CTA */}
            {!mySubscriptionLoading && mySubscription && (
              <div className="my-subscription-card">
                <h4>{t('payments.subscriptionPlans.mySubscription') || 'My subscription'}</h4>
                <p><strong>{mySubscription.plan_name}</strong> — {String(mySubscription.status).toUpperCase()}</p>
                <p>{mySubscription.ends_at ? `${t('payments.subscriptionPlans.expiresOn') || 'Expires'}: ${new Date(mySubscription.ends_at).toLocaleDateString()}` : ''}</p>
                {mySubscription.price_at_purchase != null && <p>{t('payments.subscriptionPlans.pricePaid') || 'Price paid'}: {mySubscription.price_at_purchase} {mySubscription.currency_at_purchase || 'DA'}</p>}
                {(!mySubscription.is_active) && mySubscription.plan_id && (
                  <button type="button" className="primary-btn renew-btn" onClick={() => { setPlanBlocks([{ languageCode: 'en', planId: Number(mySubscription.plan_id) }]); setShowPaymentForm(true); }}>
                    {t('payments.subscriptionPlans.renewPlan') || 'Renew plan'}
                  </button>
                )}
              </div>
            )}

            <div className="search-filter-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('payments.subscriptionPlans.searchPlaceholder')}
                  value={plansSearchTerm}
                  onChange={(e) => setPlansSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              <div className="filter-group">
                <select 
                  className="filter-select"
                  value={plansStatusFilter}
                  onChange={(e) => setPlansStatusFilter(e.target.value)}
                >
                  <option value="all">{t('payments.subscriptionPlans.filters.allStatus')}</option>
                  <option value="active">{t('payments.subscriptionPlans.filters.active')}</option>
                  <option value="inactive">{t('payments.subscriptionPlans.filters.inactive')}</option>
                  <option value="draft">{t('payments.subscriptionPlans.filters.draft')}</option>
                </select>
                <button type="button" className="filter-btn" title={t('payments.subscriptionPlans.filters.moreFilters')}>
                  <span className="material-icons">filter_list</span>
                  {t('payments.subscriptionPlans.filters.moreFilters')}
                </button>
              </div>
            </div>

            <div className="plans-grid">
              {plansLoading && (
                <div className="plans-loading">{t('payments.subscriptionPlans.loading') || 'Loading plans…'}</div>
              )}
              {!plansLoading && plansError && (
                <div className="plans-error">{plansError === 'Failed to load plans' ? t('payments.subscriptionPlans.failedToLoadPlans') : plansError}</div>
              )}
              {!plansLoading && !plansError && plans.length === 0 && (
                <div className="plans-empty">{t('payments.subscriptionPlans.noPlans') || 'No plans yet. Use Plan Builder to create one.'}</div>
              )}
              {!plansLoading && !plansError && plans.length > 0 && filteredPlans.length === 0 && (
                <div className="plans-empty">{t('payments.subscriptionPlans.noPlansMatch') || 'No plans match your filters.'}</div>
              )}
              {!plansLoading && !plansError && filteredPlans.map(plan => {
                const status = plan.is_active ? 'active' : 'inactive';
                const cycle = (plan.duration_days >= 365 ? '/year' : '/month');
                const features = Array.isArray(plan.features_json) ? plan.features_json : (typeof plan.features_json === 'string' ? (() => { try { return JSON.parse(plan.features_json || '[]'); } catch { return []; } })() : []);
                return (
                  <div key={plan.id} className="plan-card">
                  <div className="plan-header">
                    <div className="plan-title">
                      <h4>{translatePlanName(plan.name)}</h4>
                        <span className={`status-badge ${status}`}>
                          {translateStatus(status)}
                      </span>
                    </div>
                      </div>
                  <div className="plan-price">
                    <div className="price-main">
                      <span className="currency">{plan.currency}</span>
                      <span className="amount">{plan.price}</span>
                        <span className="cycle">{cycle}</span>
                    </div>
                      {cycle === '/year' && (
                      <div className="price-savings">
                        <span className="material-icons">savings</span>
                        {t('payments.subscriptionPlans.saveAnnually')}
                      </div>
                    )}
                  </div>
                  <div className="plan-features">
                    <h5>{t('payments.subscriptionPlans.features')}</h5>
                    <ul>
                        {features.map((feature, index) => (
                        <li key={index}>
                          <span className="material-icons">check</span>
                          {translateFeature(feature)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="plan-actions">
                      <button type="button" className="action-btn edit" title={t('payments.subscriptionPlans.actions.editPlan')} onClick={() => openPlanBuilderForEdit(plan)} aria-label={t('payments.subscriptionPlans.actions.editPlan')}>
                        <span className="material-icons">edit</span>
                      </button>
                      <button type="button" className="action-btn duplicate" title={t('payments.subscriptionPlans.actions.duplicatePlan')} onClick={() => handleDuplicatePlan(plan)} aria-label={t('payments.subscriptionPlans.actions.duplicatePlan')}>
                        <span className="material-icons">content_copy</span>
                      </button>
                      {status === 'active' ? (
                        <button type="button" className="action-btn pause" title={t('payments.subscriptionPlans.actions.pausePlan')} onClick={() => handleTogglePlan(plan)} aria-label={t('payments.subscriptionPlans.actions.pausePlan')}>
                          <span className="material-icons">pause</span>
                        </button>
                      ) : (
                        <button type="button" className="action-btn activate" title={t('payments.subscriptionPlans.actions.activatePlan')} onClick={() => handleTogglePlan(plan)} aria-label={t('payments.subscriptionPlans.actions.activatePlan')}>
                          <span className="material-icons">play_arrow</span>
                        </button>
                      )}
                      <button type="button" className="action-btn analytics" title={t('payments.subscriptionPlans.actions.viewAnalytics')} onClick={() => alert(t('payments.subscriptionPlans.analyticsComingSoon') || 'Analytics coming soon.')} aria-label={t('payments.subscriptionPlans.actions.viewAnalytics')}>
                        <span className="material-icons">analytics</span>
                      </button>
                      <button type="button" className="action-btn delete" title={t('payments.subscriptionPlans.actions.deletePlan')} onClick={() => handleDeletePlan(plan)} aria-label={t('payments.subscriptionPlans.actions.deletePlan')}>
                        <span className="material-icons">delete</span>
                      </button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        );

      case 'transactions':
        return (
          <div className="tab-content">
            <div className="content-header">
              <h3>{t('payments.transactions.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">download</span>
                {t('payments.transactions.exportData')}
              </button>
            </div>

            <div className="search-filter-bar">
              <div className="search-box large">
                <input
                  type="text"
                  placeholder={t('payments.transactions.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              <select 
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t('payments.transactions.filters.allStatus')}</option>
                <option value="completed">{t('payments.transactions.filters.completed')}</option>
                <option value="pending">{t('payments.transactions.filters.pending')}</option>
                <option value="failed">{t('payments.transactions.filters.failed')}</option>
                <option value="refunded">{t('payments.transactions.filters.refunded')}</option>
              </select>
            </div>

            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>{t('payments.transactions.tableHeaders.transactionId')}</th>
                    <th>{t('payments.transactions.tableHeaders.user')}</th>
                    <th>{t('payments.transactions.tableHeaders.plan')}</th>
                    <th>{t('payments.transactions.tableHeaders.amount')}</th>
                    <th>{t('payments.transactions.tableHeaders.status')}</th>
                    <th>{t('payments.transactions.tableHeaders.paymentMethod')}</th>
                    <th>{t('payments.transactions.tableHeaders.date')}</th>
                    <th>{t('payments.transactions.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(transaction => (
                    <tr key={transaction.id}>
                      <td>{transaction.id}</td>
                      <td>{transaction.user}</td>
                      <td>{transaction.plan}</td>
                      <td>${transaction.amount}</td>
                      <td>
                        <span className={`status-badge ${transaction.status}`}>
                          {translateStatus(transaction.status)}
                        </span>
                      </td>
                      <td>
                        <span className={`payment-method ${transaction.paymentMethod}`}>
                          {translatePaymentMethod(transaction.paymentMethod)}
                        </span>
                      </td>
                      <td>{transaction.date}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn view" title={t('payments.transactions.actions.viewDetails')}>
                            <span className="material-icons">visibility</span>
                          </button>
                          <button className="action-btn print" title={t('payments.transactions.actions.printReceipt')}>
                            <span className="material-icons">print</span>
                          </button>
                          {/* Hide refund button for agents and school partners (view-only) */}
                          {userRole !== 'agent' && userRole !== 'school' && (
                          <button className="action-btn refund" title={t('payments.transactions.actions.refund')}>
                            <span className="material-icons">money_off</span>
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'manual-payments':
        // Hide "Add Payment" button for moderators (they can only approve/reject, not create)
        // For agents and school partners, this is their "My Payments" view (only shows their own payments)
        const canCreatePayment = userRole !== 'moderator';
        const isAgentView = userRole === 'agent' || userRole === 'school';
        
        // Filter payments by status and search term
        const filteredPayments = manualPayments.filter(payment => {
          // Filter by status
          if (statusFilter !== 'all' && payment.status !== statusFilter) {
            return false;
          }
          // Filter by search term
          if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const customerName = `${payment.customer_first_name || ''} ${payment.customer_last_name || ''}`.toLowerCase();
            const email = (payment.customer_email || '').toLowerCase();
            const description = (payment.description || '').toLowerCase();
            const paymentRequestNumber = (payment.payment_request_number || '').toLowerCase();
            
            return customerName.includes(searchLower) ||
                   email.includes(searchLower) ||
                   description.includes(searchLower) ||
                   paymentRequestNumber.includes(searchLower);
          }
          return true;
        });
        
        return (
          <div className="tab-content">
            <div className="content-header">
              <h3>{isAgentView ? 'My Payments' : t('payments.manualPayments.title')}</h3>
              <div className="content-header-actions">
                {canCreatePayment && (
                  <>
                    <button className="secondary-btn" onClick={() => setShowAddUserForm(true)} title={t('payments.manualPayments.addUserTitle') || 'Add a customer who does not have an account'}>
                      <span className="material-icons">person_add</span>
                      {t('userManagement.addUser')}
                    </button>
                    <button className="primary-btn" onClick={() => setShowPaymentForm(true)}>
                <span className="material-icons">add</span>
                {t('payments.manualPayments.addPayment')}
              </button>
                  </>
                )}
              </div>
            </div>

            <div className="search-filter-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('payments.manualPayments.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              <select 
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t('payments.manualPayments.filters.allStatus')}</option>
                <option value="pending">{t('payments.manualPayments.filters.pending')}</option>
                <option value="approved">{t('payments.manualPayments.filters.approved')}</option>
                <option value="rejected">{t('payments.manualPayments.filters.rejected')}</option>
              </select>
            </div>

            {loadingPayments && (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>Loading payment requests...</p>
              </div>
            )}
            
            {paymentsError && (
              <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>
                <p>Error: {paymentsError}</p>
                <button onClick={fetchManualPayments} className="secondary-btn" style={{ marginTop: '10px' }}>
                  Retry
                </button>
              </div>
            )}

            {!loadingPayments && !paymentsError && (
              <>
                {filteredPayments.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    <p>{manualPayments.length === 0 ? 'No payment requests found' : 'No payments match your filters'}</p>
                  </div>
                ) : (
            <div className="manual-payments-grid">
                    {filteredPayments.map(payment => {
                      const canApproveReject = ['admin', 'moderator'].includes(userRole) && payment.status === 'pending';
                      const customerName = payment.customer_first_name && payment.customer_last_name
                        ? `${payment.customer_first_name} ${payment.customer_last_name}`
                        : payment.customer_email || 'Unknown Customer';
                      
                      return (
                <div key={payment.id} className="payment-card">
                  <div className="payment-header">
                            <h4>{customerName}</h4>
                      <span className={`status-badge ${payment.status}`}>
                      {translateStatus(payment.status)}
                    </span>
                  </div>
                  
                  <div className="payment-details">
                            <div className="detail-row">
                              <span className="label">Payment Request #:</span>
                              <span className="value">{payment.payment_request_number || `MP-${payment.id}`}</span>
                            </div>
                    <div className="detail-row">
                      <span className="label">Amount:</span>
                              <span className="value">{payment.amount} {payment.currency || 'DA'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">{t('payments.manualPayments.labels.description')}</span>
                              <span className="value">{payment.description || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Date:</span>
                              <span className="value">{payment.payment_date || payment.created_at || 'N/A'}</span>
                    </div>
                            {payment.agent_name && (
                              <div className="detail-row">
                                <span className="label">Created by:</span>
                                <span className="value">{payment.agent_name}</span>
                              </div>
                            )}
                  </div>

                          {payment.receipt_image_url && (
                  <div className="receipt-preview">
                    <h5>{t('payments.manualPayments.receiptPreview')}</h5>
                    <div className="receipt-image">
                                <img src={payment.receipt_image_url} alt="Receipt" onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }} />
                                <button 
                                  className="view-btn" 
                                  onClick={() => window.open(payment.receipt_image_url, '_blank')}
                                  style={{ display: 'none' }}
                                >
                        <span className="material-icons">visibility</span>
                      </button>
                    </div>
                  </div>
                          )}

                  <div className="payment-actions">
                            {canApproveReject && (
                              <>
                                <button 
                                  className="action-btn approve"
                                  onClick={async () => {
                                    if (window.confirm('Are you sure you want to approve this payment request?')) {
                                      try {
                                        const token = getDashboardToken();
                                        const response = await fetch(`${API_BASE_URL}/api/manual-payments/${payment.id}/approve`, {
                                          method: 'POST',
                                          headers: {
                                            'Authorization': `Bearer ${token}`,
                                            'Content-Type': 'application/json'
                                          }
                                        });
                                        
                                        if (response.ok) {
                                          const result = await response.json();
                                          alert('Payment approved successfully!');
                                          fetchManualPayments(); // Refresh list
                                        } else {
                                          const error = await response.json();
                                          alert('Error: ' + (error.error || error.message || 'Failed to approve payment'));
                                        }
                                      } catch (error) {
                                        console.error('Error approving payment:', error);
                                        alert('Error approving payment: ' + error.message);
                                      }
                                    }
                                  }}
                                >
                                  {t('payments.manualPayments.actions.approve')}
                                </button>
                                <button 
                                  className="action-btn reject"
                                  onClick={async () => {
                                    const reason = window.prompt('Please enter rejection reason:');
                                    if (reason && reason.trim()) {
                                      try {
                                        const token = getDashboardToken();
                                        const response = await fetch(`${API_BASE_URL}/api/manual-payments/${payment.id}/reject`, {
                                          method: 'POST',
                                          headers: {
                                            'Authorization': `Bearer ${token}`,
                                            'Content-Type': 'application/json'
                                          },
                                          body: JSON.stringify({ rejection_reason: reason.trim() })
                                        });
                                        
                                        if (response.ok) {
                                          alert('Payment rejected successfully!');
                                          fetchManualPayments(); // Refresh list
                                        } else {
                                          const error = await response.json();
                                          alert('Error: ' + (error.error || error.message || 'Failed to reject payment'));
                                        }
                                      } catch (error) {
                                        console.error('Error rejecting payment:', error);
                                        alert('Error rejecting payment: ' + error.message);
                                      }
                                    } else if (reason !== null) {
                                      alert('Rejection reason is required');
                                    }
                                  }}
                                >
                                  {t('payments.manualPayments.actions.reject')}
                                </button>
                              </>
                            )}
                            {payment.status !== 'pending' && (
                              <div style={{ padding: '10px', fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                                {payment.status === 'approved' && payment.moderator_name && (
                                  <div>✅ Approved by: {payment.moderator_name} on {payment.approved_at || 'N/A'}</div>
                                )}
                                {payment.status === 'rejected' && payment.rejection_reason && (
                                  <div>❌ Rejected: {payment.rejection_reason}</div>
                                )}
                  </div>
                            )}
                            <button 
                              className="action-btn view"
                              onClick={() => {
                                setSelectedPaymentDetails(payment);
                              }}
                            >
                              {t('payments.manualPayments.actions.viewDetails')}
                            </button>
                </div>
            </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 'receipts':
        // Filter payments for receipts (only approved and pending can have receipts)
        const filteredReceipts = manualPayments.filter(payment => {
          // Filter by status (all, approved, pending, rejected)
          if (receiptStatusFilter !== 'all' && payment.status !== receiptStatusFilter) {
            return false;
          }
          // Filter by search term (customer name, email, phone, payment request number)
          if (receiptSearchTerm) {
            const searchLower = receiptSearchTerm.toLowerCase();
            const customerName = `${payment.customer_first_name || ''} ${payment.customer_last_name || ''}`.toLowerCase();
            const email = (payment.customer_email || '').toLowerCase();
            const phone = (payment.customer_phone || '').toLowerCase();
            const paymentRequestNumber = (payment.payment_request_number || '').toLowerCase();
            
            return customerName.includes(searchLower) ||
                   email.includes(searchLower) ||
                   phone.includes(searchLower) ||
                   paymentRequestNumber.includes(searchLower);
          }
          return true;
        });

        // Format date helper
        const formatDate = (dateString) => {
          if (!dateString) return 'N/A';
          try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
          } catch (e) {
            return dateString;
          }
        };

        // Print receipt function
        const handlePrintReceipt = (payment, format) => {
          const isReceipt = format === 'receipt';
          const printWindow = window.open('', '_blank');
          const customerName = payment.customer_first_name && payment.customer_last_name
            ? `${payment.customer_first_name} ${payment.customer_last_name}`
            : payment.customer_email || 'Unknown Customer';
          const baseCss = `
                body { font-family: Arial, sans-serif; font-weight: 500; margin: 0 auto; color: #000; }
                .receipt-header { text-align: center; border-bottom: 2px solid #000; }
                .receipt-header h2 { margin: 0; color: #000; font-weight: 600; }
                .receipt-header p { margin: 4px 0; color: #333; font-weight: 600; }
                .receipt-body { margin: 15px 0; }
                .info-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #ccc; }
                .info-row .label { font-weight: 600; color: #333; }
                .info-row .value { color: #000; }
                .receipt-footer { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 2px solid #000; color: #333; }
                .receipt-footer p { margin: 0; font-weight: 500; }`;
          const a4Css = `
                body { padding: 20px; max-width: 180mm; }
                .receipt-print-wrap { max-width: 180mm; }
                .receipt-header { padding-bottom: 12px; margin-bottom: 15px; }
                .receipt-header h2 { font-size: 22pt; }
                .receipt-header p { font-size: 14pt; }
                .receipt-body .info-row { padding: 10px 0; }
                @media print {
                  @page { size: A4; margin: 15mm; }
                  body { margin: 0 !important; padding: 15mm !important; font-size: 11pt; max-width: none !important; }
                  .receipt-print-wrap { max-width: 100% !important; }
                }`;
          const receiptCss = `
                body { padding: 20px; max-width: 80mm; font-weight: 600; }
                .receipt-print-wrap { max-width: 80mm; }
                .receipt-header { padding-bottom: 12px; margin-bottom: 15px; }
                .receipt-header h2 { font-size: 18pt; font-weight: 700; }
                .receipt-header p { font-size: 13pt; font-weight: 700; }
                .info-row .label { font-weight: 700; }
                .info-row .value { font-weight: 600; }
                .receipt-footer p { font-weight: 600; }
                .info-row { padding: 8px 0; }
                @media print {
                  @page { size: 80mm auto; margin: 5mm; }
                  body { margin: 0 !important; padding: 5mm !important; width: 80mm !important; max-width: 80mm !important; font-size: 12pt !important; font-weight: 600 !important; }
                  .receipt-print-wrap { width: 80mm !important; max-width: 80mm !important; }
                  .receipt-header h2 { font-size: 18pt !important; font-weight: 700 !important; }
                  .receipt-header p { font-size: 13pt !important; font-weight: 700 !important; }
                  .info-row .label { font-weight: 700 !important; }
                  .info-row .value { font-weight: 600 !important; }
                  .receipt-footer p { font-weight: 600 !important; }
                }`;
          const printCss = baseCss + (isReceipt ? receiptCss : a4Css);
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>Receipt - ${payment.payment_request_number || payment.id}</title>
              <style>${printCss}</style>
            </head>
            <body>
              <div class="receipt-print-wrap">
              <div class="receipt-header">
                <h2>Syllera Learning Platform</h2>
                <p>Official Receipt</p>
              </div>
              <div class="receipt-body">
                <div class="info-row">
                  <span class="label">Receipt #:</span>
                  <span class="value">${payment.payment_request_number || `RCP-${payment.id}`}</span>
                </div>
                <div class="info-row">
                  <span class="label">Date:</span>
                  <span class="value">${formatDate(payment.payment_date || payment.created_at)}</span>
                </div>
                <div class="info-row">
                  <span class="label">Customer:</span>
                  <span class="value">${customerName}</span>
                </div>
                ${payment.customer_email ? `
                <div class="info-row">
                  <span class="label">Email:</span>
                  <span class="value">${payment.customer_email}</span>
                </div>
                ` : ''}
                ${payment.customer_phone ? `
                <div class="info-row">
                  <span class="label">Phone:</span>
                  <span class="value">${payment.customer_phone}</span>
                </div>
                ` : ''}
                <div class="info-row">
                  <span class="label">Service:</span>
                  <span class="value">${payment.description || 'Manual Payment'}</span>
                </div>
                <div class="info-row">
                  <span class="label">Amount:</span>
                  <span class="value">${payment.amount} ${payment.currency || 'DA'}</span>
                </div>
                <div class="info-row">
                  <span class="label">Status:</span>
                  <span class="value">${payment.status.toUpperCase()}</span>
                </div>
                ${payment.approved_by ? `
                <div class="info-row">
                  <span class="label">Approved by:</span>
                  <span class="value">${payment.approved_by}</span>
                </div>
                ` : ''}
                ${payment.approved_at ? `
                <div class="info-row">
                  <span class="label">Approved on:</span>
                  <span class="value">${formatDate(payment.approved_at)}</span>
                </div>
                ` : ''}
              </div>
              <div class="receipt-footer">
                <p>Thank you for choosing Syllera Learning Platform!</p>
              </div>
              </div>
            </body>
            </html>
          `);
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };

        return (
          <div className="tab-content">
            <div className="content-header">
              <h3>{t('payments.receipts.title')}</h3>
            </div>

            <div className="receipt-search-section">
              <div className="search-filter-bar">
                <div className="search-box large">
                  <input
                    type="text"
                    placeholder={t('payments.receipts.searchPlaceholder')}
                    value={receiptSearchTerm}
                    onChange={(e) => setReceiptSearchTerm(e.target.value)}
                  />
                  <span className="material-icons">search</span>
                </div>
                <select 
                  className="filter-select"
                  value={receiptStatusFilter}
                  onChange={(e) => setReceiptStatusFilter(e.target.value)}
                >
                  <option value="all">{t('payments.receipts.filters.allReceipts')}</option>
                  <option value="approved">{t('payments.manualPayments.filters.approved')}</option>
                  <option value="pending">{t('payments.manualPayments.filters.pending')}</option>
                  <option value="rejected">{t('payments.manualPayments.filters.rejected')}</option>
                </select>
              </div>
            </div>

            {loadingPayments && (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>Loading receipts...</p>
              </div>
            )}

            {paymentsError && (
              <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>
                <p>Error: {paymentsError}</p>
                <button onClick={fetchManualPayments} className="secondary-btn" style={{ marginTop: '10px' }}>
                  Retry
                </button>
              </div>
            )}

            {!loadingPayments && !paymentsError && (
              <>
                {filteredReceipts.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    <p>{manualPayments.length === 0 ? 'No receipts found' : 'No receipts match your search criteria'}</p>
                  </div>
                ) : (
                  <div className="receipts-list-section">
                    {filteredReceipts.map(payment => {
                      const customerName = payment.customer_first_name && payment.customer_last_name
                        ? `${payment.customer_first_name} ${payment.customer_last_name}`
                        : payment.customer_email || 'Unknown Customer';
                      
                      return (
                        <div key={payment.id} className="receipt-item-card" onClick={() => setSelectedReceipt(payment)}>
                          <div className="receipt-item-header">
                            <div>
                              <h4>{customerName}</h4>
                              <p className="receipt-meta">{payment.payment_request_number || `RCP-${payment.id}`} • {formatDate(payment.payment_date || payment.created_at)}</p>
                            </div>
                            <span className={`status-badge ${payment.status}`}>
                              {translateStatus(payment.status)}
                            </span>
                          </div>
                          <div className="receipt-item-body">
                            <div className="receipt-item-info">
                              <span className="label">Amount:</span>
                              <span className="value">{payment.amount} {payment.currency || 'DA'}</span>
                            </div>
                            {payment.description && (
                              <div className="receipt-item-info">
                                <span className="label">Description:</span>
                                <span className="value">{payment.description}</span>
                              </div>
                            )}
                          </div>
                          <div className="receipt-item-actions">
                            <button 
                              className="action-btn print" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePrintReceipt(payment, 'a4');
                              }}
                              title={t('payments.receipts.actions.printA4')}
                            >
                              <span className="material-icons">print</span>
                              {t('payments.receipts.actions.printA4')}
                            </button>
                            <button 
                              className="action-btn print receipt-print-small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePrintReceipt(payment, 'receipt');
                              }}
                              title={t('payments.receipts.actions.printReceipt')}
                            >
                              <span className="material-icons">receipt</span>
                              {t('payments.receipts.actions.printReceipt')}
                            </button>
                            {payment.receipt_image_url && (
                              <button 
                                className="action-btn view" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(payment.receipt_image_url, '_blank');
                                }}
                              >
                                <span className="material-icons">visibility</span>
                                View Receipt Image
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Receipt Preview Modal */}
                {selectedReceipt && (
                  <div className="modal-overlay" onClick={() => setSelectedReceipt(null)}>
                    <div className="modal-content receipt-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-header">
                        <h3>Receipt Details</h3>
                        <button className="modal-close" onClick={() => setSelectedReceipt(null)}>
                          <span className="material-icons">close</span>
                        </button>
                      </div>
                      <div className="modal-body">
              <div className="receipt-template">
                <div className="receipt-header">
                  <h2>{t('payments.receipts.template.platformName')}</h2>
                  <p>{t('payments.receipts.template.officialReceipt')}</p>
                </div>
                
                <div className="receipt-body">
                  <div className="receipt-info">
                    <div className="info-row">
                      <span className="label">{t('payments.receipts.labels.receiptNumber')}</span>
                                <span className="value">{selectedReceipt.payment_request_number || `RCP-${selectedReceipt.id}`}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Date:</span>
                                <span className="value">{formatDate(selectedReceipt.payment_date || selectedReceipt.created_at)}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">{t('payments.receipts.labels.customer')}</span>
                                <span className="value">
                                  {selectedReceipt.customer_first_name && selectedReceipt.customer_last_name
                                    ? `${selectedReceipt.customer_first_name} ${selectedReceipt.customer_last_name}`
                                    : selectedReceipt.customer_email || 'Unknown Customer'}
                                </span>
                    </div>
                              {selectedReceipt.customer_email && (
                                <div className="info-row">
                                  <span className="label">Email:</span>
                                  <span className="value email-value">{selectedReceipt.customer_email}</span>
                                </div>
                              )}
                              {selectedReceipt.customer_phone && (
                                <div className="info-row">
                                  <span className="label">Phone:</span>
                                  <span className="value">{selectedReceipt.customer_phone}</span>
                                </div>
                              )}
                    <div className="info-row">
                      <span className="label">{t('payments.receipts.labels.service')}</span>
                                <span className="value">{selectedReceipt.description || 'Manual Payment'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Amount:</span>
                                <span className="value amount-value">{selectedReceipt.amount} {selectedReceipt.currency || 'DA'}</span>
                    </div>
                              <div className="info-row">
                                <span className="label">Status:</span>
                                <span className="value">
                                  <span className={`status-badge ${selectedReceipt.status}`}>
                                    {translateStatus(selectedReceipt.status)}
                                  </span>
                                </span>
                              </div>
                              {selectedReceipt.approved_by && (
                                <div className="info-row">
                                  <span className="label">Approved by:</span>
                                  <span className="value">{selectedReceipt.approved_by}</span>
                                </div>
                              )}
                              {selectedReceipt.approved_at && (
                                <div className="info-row">
                                  <span className="label">Approved on:</span>
                                  <span className="value">{formatDate(selectedReceipt.approved_at)}</span>
                                </div>
                              )}
                  </div>
                </div>

                <div className="receipt-footer">
                  <p>{t('payments.receipts.template.thankYou')}</p>
                </div>

                <div className="receipt-actions">
                            <button 
                              className="action-btn print" 
                              onClick={() => handlePrintReceipt(selectedReceipt, 'a4')}
                            >
                              <span className="material-icons">print</span>
                              {t('payments.receipts.actions.printA4')}
                            </button>
                            <button 
                              className="action-btn print receipt-print-small"
                              onClick={() => handlePrintReceipt(selectedReceipt, 'receipt')}
                            >
                              <span className="material-icons">receipt</span>
                              {t('payments.receipts.actions.printReceipt')}
                            </button>
                            {selectedReceipt.receipt_image_url && (
                              <button 
                                className="action-btn view" 
                                onClick={() => window.open(selectedReceipt.receipt_image_url, '_blank')}
                              >
                                <span className="material-icons">visibility</span>
                                View Receipt Image
                  </button>
                            )}
                </div>
              </div>
            </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      default:
        return <div className="tab-content">Select a tab to view content</div>;
    }
  };





  return (
    <div id="payments" className={`section ${isSingleTabView ? 'payments-single-tab' : ''}`}>
      {!isSingleTabView && (
        <div className="tabs-container">
          <div className="tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="material-icons">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="content-area">
        {renderTabContent()}
      </div>


      
      {/* Plan Builder Modal (Stage 4: create + edit) */}
      {showPlanBuilder && (
        <div className="modal-overlay">
          <div className="modal-content plan-builder-modal">
            <div className="modal-header">
              <h3>{editingPlanId ? t('payments.subscriptionPlans.editPlan') : t('payments.subscriptionPlans.planBuilderModal.title')}</h3>
              <button className="modal-close" onClick={closePlanBuilder} aria-label="Close">
                <span className="material-icons">close</span>
              </button>
            </div>
            
            <div className="modal-body">
              {/* 1. Basic Information */}
              <div className="form-section">
                <h4>{t('payments.subscriptionPlans.planBuilderModal.basicInfo') || 'Basic Information'}</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('payments.subscriptionPlans.planBuilderModal.planName')} *</label>
                    <input
                      type="text"
                      value={planBuilderData.name}
                      onChange={(e) => setPlanBuilderData({ ...planBuilderData, name: e.target.value })}
                      placeholder={t('payments.subscriptionPlans.planBuilderModal.planNamePlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>Language *</label>
                    <select
                      value={planBuilderData.languageCode || 'all'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPlanBuilderData({ ...planBuilderData, languageCode: v });
                      }}
                    >
                      {languageOptions.map((l) => (
                        <option key={l.code} value={l.code}>{l.label}</option>
                      ))}
                    </select>
                    <button type="button" className="secondary-btn" style={{ marginTop: 8 }} onClick={() => openAddLanguagePicker('planBuilder')}>
                      Add language
                    </button>
                    {showAddLanguagePicker.open && showAddLanguagePicker.target === 'planBuilder' && (
                      <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                        <select
                          className="form-input"
                          value={pickedExtraLanguage}
                          onChange={(e) => {
                            const v = e.target.value;
                            setPickedExtraLanguage(v);
                            if (v) applyPickedLanguage(v);
                          }}
                        >
                          <option value="">— Select language to add —</option>
                          {availableExtraLanguages.map((l) => (
                            <option key={l.code} value={l.code}>{l.label}</option>
                          ))}
                        </select>
                        <button type="button" className="secondary-btn" onClick={closeAddLanguagePicker}>Cancel</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('payments.subscriptionPlans.planBuilderModal.featureType') || 'Feature Type'} *</label>
                    <select
                      value={planBuilderData.featureType}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPlanBuilderData({
                          ...planBuilderData,
                          featureType: v,
                          selectedFeatures: v === 'single' && planBuilderData.selectedFeatures.length > 1 ? [planBuilderData.selectedFeatures[0]] : planBuilderData.selectedFeatures
                        });
                      }}
                    >
                      <option value="single">{t('payments.subscriptionPlans.planBuilderModal.singleFeature')}</option>
                      <option value="multi">{t('payments.subscriptionPlans.planBuilderModal.multiFeature') || 'Multi-Feature'}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('payments.subscriptionPlans.planBuilderModal.userType') || 'User Type'} *</label>
                    <select
                      value={planBuilderData.userType}
                      onChange={(e) => setPlanBuilderData({ ...planBuilderData, userType: e.target.value })}
                    >
                      <option value="individual">{t('payments.subscriptionPlans.planBuilderModal.individual') || 'Individual'}</option>
                      <option value="family">{t('payments.subscriptionPlans.planBuilderModal.family') || 'Family'}</option>
                    </select>
                  </div>
                  {planBuilderData.userType === 'family' && (
                    <div className="form-group">
                      <label>{t('payments.subscriptionPlans.planBuilderModal.maxUsers') || 'Max Users'}</label>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={planBuilderData.maxUsers ?? 4}
                        onChange={(e) => setPlanBuilderData({ ...planBuilderData, maxUsers: parseInt(e.target.value, 10) || 4 })}
                        placeholder="4"
                      />
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>{t('payments.subscriptionPlans.planBuilderModal.description')} <span style={{ fontWeight: 400, color: '#666' }}>({t('payments.subscriptionPlans.planBuilderModal.autoFilledEditable') || 'auto-filled from features, editable'})</span></label>
                  <textarea
                    value={planBuilderData.description}
                    onChange={(e) => setPlanBuilderData({ ...planBuilderData, description: e.target.value, descriptionAutoFilled: false })}
                    placeholder={t('payments.subscriptionPlans.planBuilderModal.descriptionPlaceholder')}
                    rows="3"
                  />
                </div>
              </div>

              {/* 2. Feature Selection */}
              <div className="form-section">
                <h4>{t('payments.subscriptionPlans.planBuilderModal.featureSelection')}</h4>
                {planBuilderData.featureType === 'single' && (
                  <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: 'var(--text-secondary, #666)' }}>
                    {t('payments.subscriptionPlans.planBuilderModal.singleFeatureHint') || 'Select only one feature.'}
                  </p>
                )}
                {/* Educational Levels Vocabulary */}
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="feature-group-label">
                    <input
                      type="checkbox"
                      checked={planBuilderData.selectedFeatures.includes('educational_vocab')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const isSingle = planBuilderData.featureType === 'single';
                        setPlanBuilderData({
                          ...planBuilderData,
                          selectedFeatures: checked
                            ? (isSingle ? ['educational_vocab'] : [...planBuilderData.selectedFeatures, 'educational_vocab'])
                            : planBuilderData.selectedFeatures.filter((id) => id !== 'educational_vocab'),
                          ...(checked ? {} : { selectedEducationalYearLevelIds: [] })
                        });
                      }}
                    />
                    <span className="material-icons" style={{ marginRight: 6 }}>menu_book</span>
                    {t('payments.subscriptionPlans.planBuilderModal.educationalLevelsVocabularyLabel')}
                  </label>
                {planBuilderData.selectedFeatures.includes('educational_vocab') && (
                  <div style={{ marginTop: 10 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-secondary, #666)' }}>
                      {t('payments.subscriptionPlans.planBuilderModal.yearLevelsRequiredHint')}
                    </p>
                    {(() => {
                      const selected = planBuilderData.selectedEducationalYearLevelIds || [];
                      const order = ['primary', 'middle', 'high'];
                      const labels = {
                        primary: t('payments.subscriptionPlans.planBuilderModal.primarySchool'),
                        middle: t('payments.subscriptionPlans.planBuilderModal.middleSchool'),
                        high: t('payments.subscriptionPlans.planBuilderModal.highSchool')
                      };
                      const norm = (v) => String(v || '').toLowerCase().replace(/_/g, '');
                      const bySchool = order.reduce((acc, key) => {
                        acc[key] = (yearLevelsForPlanBuilder || []).filter((yl) => {
                          const s = norm(yl.school_level);
                          return s === key || s === key + 'school';
                        });
                        return acc;
                      }, {});
                      const toggleGroup = (ids, checked) => {
                        const next = checked
                          ? [...new Set([...selected, ...ids])]
                          : selected.filter((id) => !ids.includes(id));
                        setPlanBuilderData({ ...planBuilderData, selectedEducationalYearLevelIds: next });
                      };
                      const toggleOne = (id, checked) => {
                        const next = checked ? [...selected, id] : selected.filter((x) => x !== id);
                        setPlanBuilderData({ ...planBuilderData, selectedEducationalYearLevelIds: next });
                      };
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {order.map((key) => {
                            const levels = bySchool[key];
                            if (!levels.length) return null;
                            const ids = levels.map((yl) => yl.id);
                            const allSelected = ids.every((id) => selected.includes(id));
                            return (
                              <div key={key} style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '12px', background: '#fafafa' }}>
                                <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                                  <span>{labels[key] || key}</span>
                                  <label style={{ fontWeight: 400, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                      type="checkbox"
                                      checked={allSelected}
                                      onChange={(e) => toggleGroup(ids, e.target.checked)}
                                    />
                                    {t('payments.subscriptionPlans.planBuilderModal.selectAll')}
                                  </label>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                  {levels.map((yl) => (
                                    <label key={yl.id} className="feature-checkbox" style={{ marginRight: '12px' }}>
                                      <input
                                        type="checkbox"
                                        checked={selected.includes(yl.id)}
                                        onChange={(e) => toggleOne(yl.id, e.target.checked)}
                                      />
                                      <span>{yl.year_name || `Year ${yl.year_order ?? levels.indexOf(yl) + 1}`}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
                </div>
                {/* International Exams Vocabulary */}
                <div className="form-group" style={{ marginTop: 16 }}>
                  <label className="feature-group-label">
                    <input
                      type="checkbox"
                      checked={planBuilderData.selectedFeatures.includes('exam_vocab')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const isSingle = planBuilderData.featureType === 'single';
                        setPlanBuilderData({
                          ...planBuilderData,
                          selectedFeatures: checked
                            ? (isSingle ? ['exam_vocab'] : [...planBuilderData.selectedFeatures, 'exam_vocab'])
                            : planBuilderData.selectedFeatures.filter((id) => id !== 'exam_vocab'),
                          ...(checked ? {} : { selectedExamCodes: [] })
                        });
                      }}
                    />
                    <span className="material-icons" style={{ marginRight: 6 }}>assignment</span>
                    {t('payments.subscriptionPlans.planBuilderModal.internationalExamsVocabularyLabel')}
                  </label>
                {planBuilderData.selectedFeatures.includes('exam_vocab') && (
                  <div style={{ marginTop: 8 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-secondary, #666)' }}>{t('payments.subscriptionPlans.planBuilderModal.addExamLabel')}</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {PLAN_BUILDER_EXAM_OPTIONS.map((code) => {
                        const selected = (planBuilderData.selectedExamCodes || []).includes(code);
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() => {
                              const current = planBuilderData.selectedExamCodes || [];
                              const next = selected ? current.filter((c) => c !== code) : [...current, code];
                              setPlanBuilderData({ ...planBuilderData, selectedExamCodes: next });
                            }}
                            style={{
                              padding: '6px 14px',
                              borderRadius: '20px',
                              border: selected ? '2px solid var(--primary, #1976d2)' : '1px solid #ccc',
                              background: selected ? 'var(--primary, #1976d2)' : '#fff',
                              color: selected ? '#fff' : '#333',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            + {code}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                </div>

                {/* Other Features */}
                <div className="form-group" style={{ marginTop: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>{t('payments.subscriptionPlans.planBuilderModal.otherFeatures') || 'Other Features'}</label>
                  <div className="feature-selection-grid" style={{ flexWrap: 'wrap' }}>
                    {PLAN_BUILDER_OTHER_FEATURE_IDS.map((id) => {
                      const feature = availableFeatures.find((f) => f.id === id);
                      if (!feature) return null;
                      const checked = planBuilderData.selectedFeatures.includes(id);
                      return (
                        <label key={id} className="feature-checkbox">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const isSingle = planBuilderData.featureType === 'single';
                              if (e.target.checked) {
                                setPlanBuilderData({
                                  ...planBuilderData,
                                  selectedFeatures: isSingle ? [id] : [...planBuilderData.selectedFeatures, id]
                                });
                              } else {
                                setPlanBuilderData({
                                  ...planBuilderData,
                                  selectedFeatures: planBuilderData.selectedFeatures.filter((x) => x !== id)
                                });
                              }
                            }}
                          />
                          <span className="material-icons">{feature.icon}</span>
                          <span className="feature-name">{feature.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 3. Pricing & Billing */}
              <div className="form-section">
                <h4>{t('payments.subscriptionPlans.planBuilderModal.pricingBilling') || 'Pricing & Billing'}</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('payments.subscriptionPlans.planBuilderModal.price')} *</label>
                    <input
                      type="number"
                      value={planBuilderData.price}
                      onChange={(e) => setPlanBuilderData({ ...planBuilderData, price: e.target.value })}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('payments.subscriptionPlans.planBuilderModal.currency')}</label>
                    <select
                      value={planBuilderData.currency}
                      onChange={(e) => setPlanBuilderData({ ...planBuilderData, currency: e.target.value })}
                    >
                      <option value="DA">DA (د.ج)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('payments.subscriptionPlans.planBuilderModal.billingCycle')}</label>
                    <select
                      value={planBuilderData.cycle}
                      onChange={(e) => setPlanBuilderData({ ...planBuilderData, cycle: e.target.value })}
                    >
                      <option value="month">{t('payments.subscriptionPlans.planBuilderModal.monthly')}</option>
                      <option value="6months">{t('payments.subscriptionPlans.planBuilderModal.6months')}</option>
                      <option value="year">{t('payments.subscriptionPlans.planBuilderModal.yearly')}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 4. Optional */}
              <div className="form-section">
                <h4>{t('payments.subscriptionPlans.planBuilderModal.advancedOptions')}</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('payments.subscriptionPlans.planBuilderModal.trialOption')}</label>
                    <input
                      type="text"
                      value={planBuilderData.trialOption}
                      onChange={(e) => setPlanBuilderData({...planBuilderData, trialOption: e.target.value})}
                      placeholder={t('payments.subscriptionPlans.planBuilderModal.trialOptionPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('payments.subscriptionPlans.planBuilderModal.iconOptional')}</label>
                    <input
                      type="text"
                      value={planBuilderData.icon}
                      onChange={(e) => setPlanBuilderData({...planBuilderData, icon: e.target.value})}
                      placeholder={t('payments.subscriptionPlans.planBuilderModal.iconPlaceholder')}
                    />
                  </div>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={planBuilderData.isPopular}
                      onChange={(e) => setPlanBuilderData({...planBuilderData, isPopular: e.target.checked})}
                    />
                    {t('payments.subscriptionPlans.planBuilderModal.markAsPopular')}
                  </label>
                </div>
              </div>
              <div className="plan-preview">
                <h4>{t('payments.subscriptionPlans.planBuilderModal.planPreview')}</h4>
                <div className="preview-card">
                  <div className="preview-header">
                    <h5>{planBuilderData.name || 'Plan Name'}</h5>
                    {planBuilderData.isPopular && (
                      <span className="popular-badge">{t('payments.subscriptionPlans.mostPopular')}</span>
                    )}
                  </div>
                  <div className="preview-price">
                    <span className="currency">{planBuilderData.currency}</span>
                    <span className="amount">{planBuilderData.price || '0'}</span>
                    <span className="cycle">/{planBuilderData.cycle === 'year' ? t('payments.subscriptionPlans.planBuilderModal.yearly') : planBuilderData.cycle === '6months' ? t('payments.subscriptionPlans.planBuilderModal.6months') : t('payments.subscriptionPlans.planBuilderModal.monthly')}</span>
                  </div>
                  <div className="preview-features">
                    {planBuilderData.selectedFeatures.map(featureId => {
                      const feature = availableFeatures.find(f => f.id === featureId);
                      return feature ? (
                        <div key={featureId} className="preview-feature">
                          <span className="material-icons">{feature.icon}</span>
                          <span>{feature.name}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
            </div>
            
            {planBuilderError && <p className="form-error plan-builder-error">{planBuilderError}</p>}
            <div className="modal-footer">
              <button className="secondary-btn" onClick={closePlanBuilder} disabled={planBuilderSaving}>
                {t('payments.subscriptionPlans.cancel')}
              </button>
              <button className="primary-btn" onClick={savePlanBuilder} disabled={planBuilderSaving || !planBuilderCanSubmit}>
                {planBuilderSaving ? t('payments.subscriptionPlans.saving') : (editingPlanId ? t('payments.subscriptionPlans.savePlan') : t('payments.subscriptionPlans.createPlan'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <div className="modal-overlay">
          <div className="modal-content payment-form-modal">
            <div className="modal-header">
              <h3>{t('payments.manualPayments.createPaymentRequest') || 'Create Payment Request'}</h3>
              <button className="modal-close" onClick={() => setShowPaymentForm(false)}>
                <span className="material-icons">close</span>
              </button>
            </div>
            
            <div className="modal-body payment-request-modal-body">
              <form className="payment-form structured-payment-form">
                {/* 1. Customer Section */}
                <section className="payment-form-section customer-section">
                  <h4 className="section-title">1. Customer</h4>
                  <div className="form-group" style={{ position: 'relative', zIndex: 1000 }}>
                    <input
                      type="text"
                      placeholder={t('payments.manualPayments.placeholders.searchCustomer') || 'Search by email, phone, or name'}
                      className="form-input"
                      value={selectedCustomer ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || selectedCustomer.email : customerSearchTerm}
                      onChange={(e) => {
                        setCustomerSearchTerm(e.target.value);
                        if (e.target.value === '') setSelectedCustomer(null);
                        else { setSelectedCustomer(null); setShowCustomerDropdown(true); }
                      }}
                      onFocus={() => customerSearchResults.length > 0 && !selectedCustomer && setShowCustomerDropdown(true)}
                      onBlur={() => setTimeout(() => { if (!selectedCustomer) setShowCustomerDropdown(false); }, 200)}
                    />
                    {searchingCustomers && <span className="material-icons form-input-icon">hourglass_empty</span>}
                    {showCustomerDropdown && customerSearchResults.length > 0 && (
                      <div className="customer-dropdown" onMouseDown={(e) => e.preventDefault()}>
                        {customerSearchResults.map(customer => (
                          <div
                            key={customer.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSelectedCustomer(customer);
                              setCustomerSearchTerm(`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email);
                              setShowCustomerDropdown(false);
                            }}
                            className="customer-dropdown-item"
                          >
                            <div>
                              <div className="customer-dropdown-name">{customer.first_name || ''} {customer.last_name || ''}</div>
                              <div className="customer-dropdown-meta">{customer.email}{customer.phone && ` • ${customer.phone}`}</div>
                            </div>
                            <span className="material-icons">person_add</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {showCustomerDropdown && customerSearchResults.length === 0 && customerSearchTerm.length >= 2 && !searchingCustomers && (
                      <div className="customer-dropdown empty">No customers found</div>
                    )}
                    {selectedCustomer && (
                      <div className="selected-customer-chip">
                        <div><strong>✓ Selected:</strong> {selectedCustomer.first_name || ''} {selectedCustomer.last_name || ''} ({selectedCustomer.email}){selectedCustomer.phone && ` • ${selectedCustomer.phone}`}</div>
                        <button type="button" className="clear-customer-btn" onClick={() => { setSelectedCustomer(null); setCustomerSearchTerm(''); }} title="Clear">×</button>
                      </div>
                    )}
                  </div>
                </section>

                {/* 2. Language + Plan blocks */}
                <section className="payment-form-section plan-section">
                  <h4 className="section-title">2. Plans <span className="required-asterisk">*</span></h4>
                  {activePlansForForm.length === 0 ? (
                    <p className="form-hint no-plans-message">{t('payments.subscriptionPlans.noActivePlans') || 'No active plans. Add plans in Subscription Plans tab.'}</p>
                  ) : (
                    <div className="form-group">
                      <label>{t('payments.subscriptionPlans.selectPlan') || 'Select plan(s)'}</label>
                      {(() => {
                        const blocks = Array.isArray(planBlocks) && planBlocks.length ? planBlocks : [{ languageCode: 'en', planId: null }];
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {blocks.map((block, idx) => {
                              const usedPlanIds = new Set(blocks.map((b) => b.planId).filter(Boolean));
                              const usedLangs = new Set(blocks.map((b) => (b.languageCode || '').toLowerCase()).filter(Boolean));
                              const selLang = (block.languageCode || 'all').toLowerCase();
                              const filteredPlans = activePlansForForm.filter((p) => (p.language_code || 'all').toLowerCase() === selLang);

                              const canRemove = blocks.length > 1;
                              return (
                                <div key={idx} style={{ border: '1px solid #e6e6e6', borderRadius: 10, padding: 12 }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                                    <div>
                                      <label>Language</label>
                                      <select
                                        className="form-input"
                                        value={block.languageCode || ''}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setPlanBlocks((prev) => {
                                            const next = Array.isArray(prev) && prev.length ? [...prev] : [{ languageCode: 'en', planId: null }];
                                            next[idx] = { ...next[idx], languageCode: v, planId: null };
                                            return next;
                                          });
                                        }}
                                      >
                                        <option value="">— Select language —</option>
                                        {languageOptions
                                          .filter((l) => l.code !== 'all')
                                          .filter((l) => !usedLangs.has(l.code) || l.code === (block.languageCode || '').toLowerCase())
                                          .map((l) => (
                                            <option key={l.code} value={l.code}>{l.label}</option>
                                          ))}
                                      </select>
                                    </div>

                                    <div>
                                      <label>Plan</label>
                                      <select
                                        className="form-input"
                                        value={block.planId ?? ''}
                                        onChange={(e) => {
                                          const v = e.target.value ? Number(e.target.value) : null;
                                          setPlanBlocks((prev) => {
                                            const next = Array.isArray(prev) && prev.length ? [...prev] : [{ languageCode: 'en', planId: null }];
                                            next[idx] = { ...next[idx], planId: v };
                                            return next;
                                          });
                                        }}
                                        required={idx === 0}
                                        disabled={!block.languageCode || filteredPlans.length === 0}
                                      >
                                        <option value="">— Select a plan —</option>
                                        {filteredPlans
                                          .filter((p) => !usedPlanIds.has(p.id) || p.id === block.planId)
                                          .map((p) => (
                                            <option key={p.id} value={p.id}>
                                              {p.name} — {p.price} {p.currency || 'DA'}{p.duration_days >= 365 ? ' /year' : ' /month'}
                                            </option>
                                          ))}
                                      </select>
                                      {block.languageCode && filteredPlans.length === 0 && (
                                        <p className="form-hint no-plans-message" style={{ marginTop: 8 }}>
                                          No plans yet for {(languageOptions.find((l) => l.code === selLang)?.label) || selLang}.
                                        </p>
                                      )}
                                    </div>

                                    {canRemove && (
                                    <button
                                      type="button"
                                      className="secondary-btn"
                                      onClick={() => setPlanBlocks((prev) => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : [{ languageCode: 'en', planId: null }]))}
                                      title="Remove this block"
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      Remove block
                                    </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {(() => {
                        const selectedPlans = (planBlocks || [])
                          .map((b) => activePlansForForm.find((p) => p.id === b.planId))
                          .filter(Boolean);
                        if (!selectedPlans.length) return null;
                        const total = selectedPlans.reduce((sum, p) => sum + (Number(p.price) || 0), 0);
                        const currency = selectedPlans[0]?.currency || 'DA';
                        return <p className="form-hint">Amount: <strong>{total} {currency}</strong></p>;
                      })()}

                      <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="secondary-btn"
                          onClick={() => setPlanBlocks((prev) => ([...(Array.isArray(prev) && prev.length ? prev : [{ languageCode: 'en', planId: null }]), { languageCode: '', planId: null }]))}
                        >
                          <span className="material-icons">add_circle</span> Add another language & plan
                        </button>
                        {/* Omitted: Plan Builder shortcut in payment form */}
                      </div>
                    </div>
                  )}
                </section>

                {/* 4. Date & Metadata */}
                <section className="payment-form-section meta-section">
                  <h4 className="section-title">4. Date & metadata</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Payment date</label>
                      <input type="date" className="form-input" value={paymentFormDate} onChange={(e) => setPaymentFormDate(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Currency</label>
                      <select className="form-input" value={paymentFormCurrency} onChange={(e) => setPaymentFormCurrency(e.target.value)}>
                        <option value="DA">DA</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Promo code (optional)</label>
                      <input type="text" className="form-input" placeholder="e.g. WELCOME10" value={paymentFormPromo} onChange={(e) => setPaymentFormPromo((e.target.value || '').trim())} />
                    </div>
                  </div>
                  <p className="meta-hint">Reference number will be generated on submit. Agent: {user.first_name || user.email || '—'}</p>
                </section>

                {/* 5. Summary */}
                <section className="payment-form-section summary-section">
                  <h4 className="section-title">5. Summary</h4>
                  {(() => {
                    const selectedPlans = (Array.isArray(planBlocks) ? planBlocks : [])
                      .map((b) => activePlansForForm.find((p) => p.id === b.planId))
                      .filter(Boolean);
                    if (!selectedPlans.length) return <p className="summary-empty">Select at least one plan above.</p>;
                    const currency = selectedPlans[0]?.currency || paymentFormCurrency || 'DA';
                    const total = selectedPlans.reduce((sum, p) => sum + (Number(p.price) || 0), 0);
                    return (
                      <div className="summary-table-wrap">
                        <table className="summary-table">
                          <tbody>
                            {selectedPlans.map((p) => (
                              <tr key={p.id}>
                                <td><strong>Plan</strong></td>
                                <td>{p.name} ({p.duration_days >= 365 ? 'Yearly' : 'Monthly'})</td>
                              </tr>
                            ))}
                            <tr>
                              <td><strong>Total</strong></td>
                              <td><strong>{total} {currency}</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </section>

                {/* Receipt & Notes */}
                <section className="payment-form-section">
                  <div className="form-group">
                    <label>{t('payments.manualPayments.labels.receiptImage') || 'Receipt image (optional)'}</label>
                    <input type="file" accept="image/*" className="form-input" onChange={(e) => setPaymentFormReceiptFile(e.target.files?.[0] || null)} />
                  </div>
                  <div className="form-group">
                    <label>{t('payments.manualPayments.labels.notes') || 'Notes (optional)'}</label>
                    <textarea className="form-input" rows={2} placeholder={t('payments.manualPayments.placeholders.notes') || 'Any additional notes'} value={paymentFormNotes} onChange={(e) => setPaymentFormNotes(e.target.value)} />
                  </div>
                </section>
              </form>
            </div>

            <div className="modal-footer">
              <button className="secondary-btn" onClick={() => setShowPaymentForm(false)}>{t('payments.manualPayments.actions.cancel') || 'Cancel'}</button>
              {selectedCustomer && (() => {
                const selectedPlans = (Array.isArray(planBlocks) ? planBlocks : [])
                  .map((b) => activePlansForForm.find((p) => p.id === b.planId))
                  .filter(Boolean);
                if (!selectedPlans.length) return null;
                const displayCurrency = selectedPlans[0]?.currency || paymentFormCurrency || 'DA';
                const displayTotal = selectedPlans.reduce((sum, p) => sum + (Number(p.price) || 0), 0);
                const summary = selectedPlans.map((p) => p.name).join(' + ') + (paymentFormPromo ? ` | Promo: ${paymentFormPromo}` : '');
                return (
                  <button
                    type="button"
                    className="secondary-btn send-invoice-btn"
                    onClick={() => setSendInvoiceConfirm({
                      email: selectedCustomer.email,
                      customerName: `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim(),
                      summary,
                      total: displayTotal,
                      currency: displayCurrency
                    })}
                  >
                    <span className="material-icons">email</span> Send invoice
                  </button>
                );
              })()}
              <button
                className="primary-btn"
                disabled={!selectedCustomer || (Array.isArray(planBlocks) ? planBlocks.filter((b) => !!b.planId).length === 0 : true) || activePlansForForm.length === 0}
                style={{ opacity: (!selectedCustomer || (Array.isArray(planBlocks) ? planBlocks.filter((b) => !!b.planId).length === 0 : true) || activePlansForForm.length === 0) ? 0.6 : 1, cursor: (!selectedCustomer || (Array.isArray(planBlocks) ? planBlocks.filter((b) => !!b.planId).length === 0 : true) || activePlansForForm.length === 0) ? 'not-allowed' : 'pointer' }}
                onClick={async () => {
                  if (!selectedCustomer) { alert(t('payments.manualPayments.alerts.selectCustomer') || 'Please select a customer first'); return; }
                  const picked = (Array.isArray(planBlocks) ? planBlocks.map((b) => b.planId) : []).filter(Boolean);
                  if (picked.length === 0) { alert(t('payments.subscriptionPlans.selectPlanRequired') || 'Please select at least one plan.'); return; }
                  const selectedPlans = picked.map((id) => activePlansForForm.find((p) => p.id === id)).filter(Boolean);
                  if (selectedPlans.length !== picked.length) { alert(t('payments.subscriptionPlans.selectPlanRequired') || 'Please select valid plan(s).'); return; }
                  try {
                    const formData = new FormData();
                    formData.append('customer_id', selectedCustomer.id);
                    // Send multi-plan selection. Backend will calculate total from plans for safety.
                    formData.append('plan_ids', JSON.stringify(picked.map(Number)));
                    formData.append('plan_id', picked[0]); // backward compatibility / primary plan
                    const currency = selectedPlans[0]?.currency || paymentFormCurrency || 'DA';
                    const total = selectedPlans.reduce((sum, p) => sum + (Number(p.price) || 0), 0);
                    formData.append('amount', total);
                    formData.append('currency', currency);
                    let desc = selectedPlans.map((p) => `Plan: ${p.name} (${p.duration_days >= 365 ? 'Yearly' : 'Monthly'})`).join('\n');
                    if (paymentFormPromo) desc += `\nPromo: ${paymentFormPromo}`;
                    if (paymentFormNotes) desc += `\nNotes: ${paymentFormNotes}`;
                    formData.append('description', desc);
                    formData.append('payment_date', paymentFormDate);
                    if (paymentFormReceiptFile) formData.append('receipt_image', paymentFormReceiptFile);
                    const token = getDashboardToken();
                    const response = await fetch(`${API_BASE_URL}/api/manual-payments/create`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` },
                      body: formData
                    });
                    if (response.ok) {
                      const result = await response.json();
                      // ✅ SUCCESS STATE - Payment request created (amount/currency from selected plan)
                      const receiptData = {
                        receipt_number: result.data?.receipt_number || `RCP-${new Date().getFullYear()}-${String(result.data?.id || Date.now()).padStart(3, '0')}`,
                        payment_request_number: result.data?.payment_request_number || `MP-${new Date().getFullYear()}-${String(result.data?.id || Date.now()).padStart(3, '0')}`,
                        customer_id: selectedCustomer.id,
                        customer_name: `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || selectedCustomer.email,
                        customer_email: selectedCustomer.email,
                        customer_phone: selectedCustomer.phone || null,
                        amount: total,
                        currency: currency,
                        payment_method: 'Cash', // Default, can be enhanced later
                        payment_date: paymentFormDate,
                        payment_time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                        description: desc,
                        status: 'pending',
                        receipt_type: 'provisional',
                        agent_name: user.first_name || user.email || 'Agent',
                        branch: 'Main Office', // Can be enhanced with actual branch data
                        created_at: new Date().toISOString(),
                        payment_request_id: result.data?.id || null
                      };
                      
                      // Set provisional receipt and show preview
                      setProvisionalReceipt(receiptData);
                      setShowReceiptPreview(true);
                      // Close payment form
                      setShowPaymentForm(false);
                      // Refresh payments list
                      fetchManualPayments();
                    } else {
                      const err = await response.json();
                      alert('Error: ' + (err.error || err.message || 'Failed to create payment request'));
                    }
                  } catch (error) {
                    console.error('Error creating payment request:', error);
                    alert('Error creating payment request: ' + error.message);
                  }
                }}
              >
                {t('payments.manualPayments.actions.submit') || 'Create payment request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send invoice confirmation modal */}
      {sendInvoiceConfirm && (
        <div className="modal-overlay" onClick={() => setSendInvoiceConfirm(null)}>
          <div className="modal-content send-invoice-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send invoice</h3>
              <button type="button" className="modal-close" onClick={() => setSendInvoiceConfirm(null)} aria-label="Close"><span className="material-icons">close</span></button>
            </div>
            <div className="modal-body">
              <p>Send invoice to <strong>{sendInvoiceConfirm.email}</strong> ({sendInvoiceConfirm.customerName})?</p>
              <p className="send-invoice-summary" title={sendInvoiceConfirm.summary}>{sendInvoiceConfirm.summary}</p>
              <p><strong>Total: {sendInvoiceConfirm.total} {sendInvoiceConfirm.currency != null ? sendInvoiceConfirm.currency : paymentFormCurrency}</strong></p>
              <p className="meta-hint">The invoice will be sent by email from the server (same as password reset emails).</p>
            </div>
            <div className="modal-footer">
              <button className="secondary-btn" onClick={() => setSendInvoiceConfirm(null)} disabled={sendInvoiceSending}>Cancel</button>
              <button
                className="primary-btn"
                disabled={sendInvoiceSending}
                onClick={async () => {
                  setSendInvoiceSending(true);
                  try {
                    const token = getDashboardToken();
                    const response = await fetch(`${API_BASE_URL}/api/manual-payments/send-invoice`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                      body: JSON.stringify({
                        to: sendInvoiceConfirm.email,
                        customerName: sendInvoiceConfirm.customerName,
                        summary: sendInvoiceConfirm.summary,
                        total: sendInvoiceConfirm.total,
                        currency: paymentFormCurrency
                      })
                    });
                    const data = await response.json();
                    setSendInvoiceConfirm(null);
                    if (data.invoiceEmailSent) {
                      alert('Invoice email sent successfully.');
                    } else {
                      alert('Failed to send invoice email. Please try again or send manually.');
                    }
                  } catch (err) {
                    console.error('Send invoice error:', err);
                    alert('Error sending invoice: ' + (err.message || 'Please try again.'));
                  } finally {
                    setSendInvoiceSending(false);
                  }
                }}
              >
                {sendInvoiceSending ? 'Sending…' : 'Send invoice by email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Provisional Receipt Preview Modal - Only shows after successful payment request creation */}
      {showReceiptPreview && provisionalReceipt && (
        <div className="modal-overlay" onClick={() => {}}>
          <div className="modal-content receipt-preview-modal provisional-receipt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✅ Payment Request Created Successfully!</h3>
              <button className="modal-close" onClick={() => {
                setShowReceiptPreview(false);
                // Reset form after closing
                setSelectedCustomer(null);
                setCustomerSearchTerm('');
                setPaymentFormDate(new Date().toISOString().split('T')[0]);
                setPaymentFormNotes('');
                setPaymentFormPromo('');
                setPaymentFormReceiptFile(null);
                setProvisionalReceipt(null);
              }}>
                <span className="material-icons">close</span>
              </button>
            </div>
            
            <div className="modal-body">
              {/* ⚠️ PROVISIONAL RECEIPT LABEL - Big and Bold */}
              <div className="provisional-receipt-header">
                <h2 className="provisional-label">⚠️ PROVISIONAL RECEIPT</h2>
                <p className="provisional-subtitle">This receipt does NOT confirm payment approval.</p>
              </div>

              {/* Receipt Template */}
              <div className="receipt-template provisional-receipt-template" id="provisional-receipt-print">
                <div className="receipt-header provisional-header">
                  <h2>Syllera Learning Platform</h2>
                  <p className="receipt-type-label">Official Receipt</p>
                </div>
                
                <div className="receipt-body">
                  <div className="receipt-info-row">
                    <span className="label">Receipt #:</span>
                    <span className="value receipt-number">{provisionalReceipt.payment_request_number}</span>
                  </div>
                  
                  <div className="receipt-info-row">
                    <span className="label">Date:</span>
                    <span className="value">{new Date(provisionalReceipt.payment_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  
                  <div className="receipt-info-row">
                    <span className="label">Customer:</span>
                    <span className="value">{provisionalReceipt.customer_email || provisionalReceipt.customer_name}</span>
                  </div>
                  
                  {provisionalReceipt.customer_email && (
                    <div className="receipt-info-row">
                      <span className="label">Email:</span>
                      <span className="value">{provisionalReceipt.customer_email}</span>
                    </div>
                  )}
                  
                  {provisionalReceipt.description && (
                    <div className="receipt-info-row">
                      <span className="label">Service:</span>
                      <span className="value">{provisionalReceipt.description}</span>
                    </div>
                  )}
                  
                  <div className="receipt-info-row">
                    <span className="label">Amount:</span>
                    <span className="value amount-value"><strong>{parseFloat(provisionalReceipt.amount || 0).toFixed(2)} {provisionalReceipt.currency}</strong></span>
                  </div>
                  
                  <div className="receipt-info-row status-row">
                    <span className="label">Status:</span>
                    <span className="value status-badge status-pending">PENDING</span>
                  </div>
                </div>
                
                <div className="receipt-footer provisional-receipt-footer">
                  <p>Thank you for choosing Syllera Learning Platform</p>
                </div>
              </div>
            </div>
            
            <div className="modal-footer receipt-preview-actions">
              <button
                type="button"
                className="primary-btn print-provisional-btn"
                onClick={() => handlePrintProvisionalReceipt('a4')}
                disabled={printingReceipt}
              >
                {printingReceipt ? 'Printing...' : '🖨️ ' + t('payments.receipts.actions.printA4')}
              </button>
              <button
                type="button"
                className="secondary-btn print-receipt-btn"
                onClick={() => handlePrintProvisionalReceipt('receipt')}
                disabled={printingReceipt}
              >
                {printingReceipt ? 'Printing...' : '🧾 ' + t('payments.receipts.actions.printReceipt')}
              </button>
              
              <div className="receipt-actions-center-group">
                <button 
                  className="secondary-btn"
                  onClick={() => {
                    setShowReceiptPreview(false);
                    setSelectedCustomer(null);
                    setCustomerSearchTerm('');
                    setPaymentFormDate(new Date().toISOString().split('T')[0]);
                    setPaymentFormNotes('');
                    setPaymentFormPromo('');
                    setPaymentFormReceiptFile(null);
                    setProvisionalReceipt(null);
                  }}
                >
                  Create Another Payment Request
                </button>
                
                <button 
                  className="secondary-btn"
                  onClick={() => {
                    setShowReceiptPreview(false);
                    setProvisionalReceipt(null);
                  }}
                >
                  View Payment Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal (for customers without an account) */}
      {showAddUserForm && (
        <div className="modal-overlay add-user-modal-overlay" onClick={() => !addUserLoading && setShowAddUserForm(false)}>
          <div className="modal-content add-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('userManagement.forms.addNewUser') || 'Add New User'}</h3>
              <button className="modal-close" onClick={() => !addUserLoading && setShowAddUserForm(false)} disabled={addUserLoading}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <div className="modal-body">
              {addUserError && (
                <div className="error-message" style={{ marginBottom: '12px' }}>{addUserError}</div>
              )}
              <div className="add-user-form-fields">
                <input
                  type="text"
                  placeholder={t('userManagement.forms.firstName') || 'First Name *'}
                  value={addUserFormData.first_name}
                  onChange={(e) => setAddUserFormData({ ...addUserFormData, first_name: e.target.value })}
                />
                <input
                  type="text"
                  placeholder={t('userManagement.forms.lastName') || 'Last Name *'}
                  value={addUserFormData.last_name}
                  onChange={(e) => setAddUserFormData({ ...addUserFormData, last_name: e.target.value })}
                />
                <input
                  type="email"
                  placeholder={t('userManagement.forms.email') || 'Email *'}
                  value={addUserFormData.email}
                  onChange={(e) => setAddUserFormData({ ...addUserFormData, email: e.target.value })}
                />
                <input
                  type="tel"
                  placeholder={t('userManagement.forms.phone') || 'Phone *'}
                  value={addUserFormData.phone}
                  onChange={(e) => setAddUserFormData({ ...addUserFormData, phone: e.target.value })}
                  required
                />
                <input
                  type="text"
                  placeholder={t('userManagement.forms.country') || 'Country'}
                  value={addUserFormData.country}
                  onChange={(e) => setAddUserFormData({ ...addUserFormData, country: e.target.value })}
                />
                <input
                  type="text"
                  placeholder={t('userManagement.forms.city') || 'City'}
                  value={addUserFormData.city}
                  onChange={(e) => setAddUserFormData({ ...addUserFormData, city: e.target.value })}
                />
                <select
                  value={addUserFormData.role}
                  onChange={(e) => setAddUserFormData({ ...addUserFormData, role: e.target.value })}
                >
                  <option value="student">{t('userManagement.forms.student') || 'Student'}</option>
                  <option value="parent">{t('userManagement.forms.parent') || 'Parent'}</option>
                  <option value="teacher">{t('userManagement.forms.teacher') || 'Teacher'}</option>
                  <option value="agent">{t('userManagement.forms.agent') || 'Agent'}</option>
                </select>
                <select
                  value={addUserFormData.status}
                  onChange={(e) => setAddUserFormData({ ...addUserFormData, status: e.target.value })}
                >
                  <option value="active">{t('userManagement.forms.active') || 'Active'}</option>
                  <option value="inactive">{t('userManagement.forms.inactive') || 'Inactive'}</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="secondary-btn" onClick={() => !addUserLoading && setShowAddUserForm(false)} disabled={addUserLoading}>
                {t('userManagement.actions.cancel') || 'Cancel'}
              </button>
              <button className="primary-btn" onClick={createUserFromPayments} disabled={addUserLoading}>
                {addUserLoading ? (t('userManagement.adding') || 'Adding...') : (t('userManagement.addUser') || 'Add User')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ViewPaymentDetailsModal
        isOpen={!!selectedPaymentDetails}
        onClose={() => setSelectedPaymentDetails(null)}
        payment={selectedPaymentDetails}
        translateStatus={translateStatus}
      />
    </div>
  );
};

export default Payments; 